import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Sparkles, BookOpen, Terminal, Cpu, Layers, Globe, Search, Code, Image, Video, 
  Layout, Play, CheckCircle, TrendingUp, Coins, MessageSquare, FolderGit, 
  FileCode, Share2, Sliders, Eye, Settings, AlertTriangle, Workflow, 
  ClipboardList, HelpCircle, Heart, Info, Lock, Plus, Trash, ArrowRight, 
  ExternalLink, Zap, ChevronRight, RefreshCw, Send, Check
} from 'lucide-react';
import { NexusUser, WorkSuiteTask } from '../types/nexus';
import { NexusAIProductFactory } from './NexusAIProductFactory';

interface NexusGalaxyLabProps {
  user: NexusUser;
  isAdminMode: boolean;
  onAddLog: (module: string, action: string, status: 'SUCCESS' | 'WARNING' | 'DENIED' | 'PENDING_APPROVAL', details: string) => void;
  onAddTaskToWorkSuite: (task: Partial<WorkSuiteTask>) => void;
  searchCredits: number;
  onAddSearchCredits: (price: number, amount: number) => void;
  currentRole: 'admin' | 'client' | 'anonymous' | 'god-architect';
}

export const NexusGalaxyLab: React.FC<NexusGalaxyLabProps> = ({
  user,
  isAdminMode,
  onAddLog,
  onAddTaskToWorkSuite,
  searchCredits: externalSearchCredits,
  onAddSearchCredits,
  currentRole
}) => {
  // Screens navigation
  const [activeScreen, setActiveScreen] = useState<string>('home');

  // Internal states to simulate a real high-fidelity environment
  const [searchCredits, setSearchCredits] = useState<number>(externalSearchCredits || 25);
  const [aiTasksRemaining, setAiTasksRemaining] = useState<number>(user.aiTasksRemaining || 10);
  const [notebooksCount, setNotebooksCount] = useState<number>(1);
  const [savedProjectsCount, setSavedProjectsCount] = useState<number>(1);

  // local theme selector: switch between 'RESEARCH_DARK' and 'MINIMALIST_LIGHT'
  const [labTheme, setLabTheme] = useState<'RESEARCH_DARK' | 'MINIMALIST_LIGHT'>(() => {
    return (localStorage.getItem('galaxy_lab_theme') as any) || 'RESEARCH_DARK';
  });

  const toggleLabTheme = () => {
    const nextTheme = labTheme === 'RESEARCH_DARK' ? 'MINIMALIST_LIGHT' : 'RESEARCH_DARK';
    setLabTheme(nextTheme);
    localStorage.setItem('galaxy_lab_theme', nextTheme);
    onAddLog('Galaxy Lab Command', `Switched local lab ambiance to ${nextTheme}`, 'SUCCESS', 'Theme styles adjusted instantly.');
  };

  // Administrative Beneficiaries Rules Configuration (Do not use levy, tax or extraction words in public)
  const [charityConfig, setCharityConfig] = useState({
    beneficiary1Name: 'Dunfermline Community Health Trust',
    beneficiary1Percent: 1,
    beneficiary2Name: 'NexoraX Global Open Source Foundation',
    beneficiary2Percent: 1,
    beneficiary3Name: 'UK Everyday Builders Initiative',
    beneficiary3Percent: 1,
    calculationBasis: 'Eligible Net Receipts',
    vatExcluded: true,
    refundsExcluded: true,
    timePeriod: 'Annual Q2 Alignment',
    contributionCap: '£50,000 per cause',
    publicDisclosureEnabled: true
  });

  const isCharityConfigIncomplete = !charityConfig.beneficiary1Name || 
                                    !charityConfig.beneficiary2Name || 
                                    !charityConfig.beneficiary3Name || 
                                    !charityConfig.publicDisclosureEnabled;

  // Screen 2: Notebooks Data with sessionStorage persistence (auto-save)
  const [notebooks, setNotebooks] = useState(() => {
    const saved = sessionStorage.getItem('galaxy_lab_notebooks');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error("Failed to parse notebooks from sessionStorage:", e);
      }
    }
    return [
      {
        id: 'nb-1',
        title: 'Everyday Coffee Shop App Concept',
        sources: [
          { type: 'text', text: 'Target Market: Students & remote workers needing high-speed wifi & simple checkouts.' },
          { type: 'link', text: 'https://competitor-coffeeshop.co.uk' },
          { type: 'search', text: 'Trending keywords: organic pour-over, cold brew subscription' }
        ],
        editorContent: 'We will construct a simple 1-page mobile checkout web app where users pre-order coffee to reduce wait time.\n\nKey features include:\n- Interactive loyalty card\n- Instant Stripe Pay\n- Real-time order progress ticker',
        aiResponse: 'Based on your 3 sources, the action plan is grounded: 1. Launch a static pricing calculator. 2. Establish a feedback chatbot. Let me know if you would like me to generate the layout components in Spark App Builder.'
      }
    ];
  });

  const [activeNotebookId, setActiveNotebookId] = useState<string>(() => {
    return sessionStorage.getItem('galaxy_lab_active_notebook_id') || 'nb-1';
  });

  // Automatically save notebooks to sessionStorage upon any state modification
  useEffect(() => {
    sessionStorage.setItem('galaxy_lab_notebooks', JSON.stringify(notebooks));
  }, [notebooks]);

  useEffect(() => {
    sessionStorage.setItem('galaxy_lab_active_notebook_id', activeNotebookId);
  }, [activeNotebookId]);
  const [newNoteInput, setNewNoteInput] = useState<string>('');
  const [newSourceUrl, setNewSourceUrl] = useState<string>('');
  const [notebookQuery, setNotebookQuery] = useState<string>('');
  const [queryGrounded, setQueryGrounded] = useState<boolean | null>(null);

  // Screen 3: AI Prompt Studio
  const [systemInstruction, setSystemInstruction] = useState<string>('You are the NexoraX AI Studio engine. Draft concise, conversion-optimized copy using precise display typography.');
  const [userPrompt, setUserPrompt] = useState<string>('Create a premium subscription marketing landing page copy for a local yoga studio.');
  const [selectedFormat, setSelectedFormat] = useState<string>('Landing page copy');
  const [selectedModel, setSelectedModel] = useState<string>('gemini-2.5-pro');
  const [promptTemperature, setPromptTemperature] = useState<number>(0.7);
  const [promptOutput, setPromptOutput] = useState<string>('');
  const [promptHistory, setPromptHistory] = useState<Array<{prompt: string, format: string, output: string}>>([]);

  // Screen 4: Agent Mission Control
  const [missions, setMissions] = useState([
    {
      id: 'm-1',
      title: 'Analyze Competitor Pricing & Prepare Pitch Plan',
      status: 'Completed',
      progress: 100,
      artifacts: {
        plan: '1. Scan local takeaway listings. 2. Extract price margins. 3. Output structural competitive advantage pitch.',
        report: 'Competitor average is £14.50 with a legacy checkout system. Real-time checkout cuts conversion friction by 40%.'
      }
    },
    {
      id: 'm-2',
      title: 'Draft Multi-screen Corporate Service Proposal',
      status: 'Running',
      progress: 60,
      artifacts: {
        plan: '1. Structuring sections. 2. Writing visual specifications. 3. Preparing deployment checklist.',
        report: 'Analyzing user target data...'
      }
    }
  ]);
  const [targetMissionTitle, setTargetMissionTitle] = useState<string>('');
  const [missionLog, setMissionLog] = useState<string[]>([]);

  // Screen 5: App Builder Canvas
  const [appIdea, setAppIdea] = useState<string>('Pricing calculator for a wedding photography business');
  const [selectedAppTemplate, setSelectedAppTemplate] = useState<string>('Pricing Calculator');
  const [generatedAppStructure, setGeneratedAppStructure] = useState<any>(null);

  // Screen 6: Automation Flow Builder
  const [flows, setFlows] = useState([
    { id: 'flow-1', name: 'Chat Lead to WorkSuite Task', trigger: 'New chat lead', action: 'Create WorkSuite task', active: true },
    { id: 'flow-2', name: 'Search Saved to Notebook', trigger: 'New search saved', action: 'Create notebook note', active: true }
  ]);
  const [newFlowTrigger, setNewFlowTrigger] = useState<string>('New form submitted');
  const [newFlowAction, setNewFlowAction] = useState<string>('Create WorkSuite task');

  // Screen 7: Research & Search Lab
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchType, setSearchType] = useState<string>('Web search');
  const [searchHistory, setSearchHistory] = useState<string[]>([]);
  const [searchResults, setSearchResults] = useState<any[]>([]);

  // Screen 8: Code & File Workspace
  const [selectedFile, setSelectedFile] = useState<string>('index.html');
  const [filesData, setFilesData] = useState<any>({
    'index.html': `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <title>NexoraX Interactive Checkout</title>
    <link rel="stylesheet" href="https://fonts.googleapis.com/css?family=Space+Grotesk">
</head>
<body class="bg-black text-white font-sans">
    <main class="max-w-xl mx-auto py-12 px-6">
        <h1 class="text-3xl font-bold tracking-tight">NexoraX Checkouts</h1>
        <p class="text-slate-400">Secure. Infinite. Optimized.</p>
    </main>
</body>
</html>`,
    'styles.css': `:root {
  --neon-cyan: #00ffff;
  --neon-purple: #e840f3;
  --gold-premium: #ffaa00;
}`,
    'app.js': `console.log("Nexus NexoraX Module Loaded Successfully.");`
  });

  // Screen 9: Media Creation Dock
  const [mediaPrompt, setMediaPrompt] = useState<string>('Cinematic render of an automated server node float in space, digital art, neon core');
  const [mediaStyle, setMediaStyle] = useState<string>('Futuristic Tech');
  const [mediaOutputs, setMediaOutputs] = useState<Array<{type: string, preview: string, prompt: string, status: string}>>([]);

  // Screen 10: WorkSuite Project Board
  const [worksuiteTasks, setWorksuiteTasks] = useState<WorkSuiteTask[]>([
    { id: 't-1', title: 'Complete domain SSL configuration checking', project: 'Nexus Galaxy', client: 'Claire (Secure Invoice Register Office)', priority: 'URGENT', status: 'TODO' },
    { id: 't-2', title: 'Compile initial briefing files for Communications Associate', project: 'Client Resource Workflow Prep', client: 'Director Office', priority: 'HIGH', status: 'IN_PROGRESS' }
  ]);
  const [newSuiteTaskTitle, setNewSuiteTaskTitle] = useState('');

  // Screen 11: Publish & Hosting Hub
  const [customDomainInput, setCustomDomainInput] = useState<string>('my-brand.co.uk');
  const [dnsVerified, setDnsVerified] = useState<boolean>(false);
  const [hostingStatus, setHostingStatus] = useState<string>('Draft Site Saved');

  // Trigger limit warning
  const [showLimitWarning, setShowLimitWarning] = useState<boolean>(false);

  // --- LAB EXTENSIONS: Search, Sort, Filter, and Action States ---
  const [labSearchQuery, setLabSearchQuery] = useState('');
  const [labSortBy, setLabSortBy] = useState<'name' | 'tier' | 'status'>('name');
  const [labSortDir, setLabSortDir] = useState<'asc' | 'desc'>('asc');
  const [labCategoryFilter, setLabCategoryFilter] = useState<string>('ALL');
  
  // Quick Actions Execution Feedback States
  const [isCalibrating, setIsCalibrating] = useState(false);
  const [diagnosticLogs, setDiagnosticLogs] = useState<string[]>([]);

  // List of Dynamic Galaxy Lab Modules containing rich metadata for Search/Sort/Filter
  const [labModules, setLabModules] = useState([
    { id: 'notebook', name: 'Galaxy Notebook', desc: 'Synthesize research, import YouTube URLs, website sources, and ground your AI results.', icon: BookOpen, accent: 'border-purple-500/20 hover:border-purple-500/40', category: 'Notebooks', tier: 'Free', status: 'Ready' },
    { id: 'prompt', name: 'AI Prompt Studio', desc: 'Test System instructions, parameters, temperature triggers, and generate multi-format logs.', icon: Sliders, accent: 'border-cyan-500/20 hover:border-cyan-500/40', category: 'Core Developer', tier: 'Pro', status: 'Calibrated' },
    { id: 'agent', name: 'Agent Mission Control', desc: 'Deploy automated background agents to prepare competitor analysis and proposal plans.', icon: Cpu, accent: 'border-amber-500/20 hover:border-amber-500/40', category: 'Autonomous Agents', tier: 'Bronze', status: 'Active' },
    { id: 'app', name: 'App Builder Canvas', desc: 'Describe calculators, forms, and pages and draft design token structures instantly.', icon: Layout, accent: 'border-emerald-500/20 hover:border-emerald-500/40', category: 'Design/Builder', tier: 'Silver', status: 'Development' },
    { id: 'flow', name: 'Automation Flow Builder', desc: 'Connect platform web triggers to WorkSuite actions using customized logical presets.', icon: Workflow, accent: 'border-pink-500/20 hover:border-pink-500/40', category: 'Automated Operations', tier: 'Gold', status: 'Ready' },
    { id: 'search', name: 'Research & Search Lab', desc: 'Perform specialized domain intelligence audits. Purchase fast credit bundles on the go.', icon: Search, accent: 'border-teal-500/20 hover:border-teal-500/40', category: 'Information Auditing', tier: 'Free', status: 'Ready' },
    { id: 'media', name: 'Media Creation Dock', desc: 'Draft video prompt lines, voice-over briefs, UGC scripts, and high conversion copy templates.', icon: Video, accent: 'border-rose-500/20 hover:border-rose-500/45', category: 'Media & Copywriting', tier: 'Pro', status: 'Ready' },
    { id: 'publish', name: 'Publish & Hosting Hub', desc: 'Initiate deployment checkers, SSL certificate requests, and point domain servers online.', icon: Globe, accent: 'border-indigo-500/20 hover:border-indigo-500/40', category: 'Ecosystem Deployment', tier: 'VIP Lifetime', status: 'Ready' }
  ]);

  const handleLabQuickAction = (actionType: string) => {
    switch (actionType) {
      case 'DIAGNOSTIC':
        const newLogs = [
          `[${new Date().toLocaleTimeString()}] DI-01: Probing active endpoints.`,
          `[${new Date().toLocaleTimeString()}] DI-02: Routing verified through SC887095.`,
          `[${new Date().toLocaleTimeString()}] DI-03: VAT eligibility checked. Net Settlement verified.`,
          `[${new Date().toLocaleTimeString()}] DI-04: Secure Invoice Register synchronizer responding OK.`
        ];
        setDiagnosticLogs(prev => [...newLogs, ...prev]);
        onAddLog('Galaxy Lab Diagnostic', 'Executed full system diagnostic test', 'SUCCESS', 'All nodes returning 0.8s standard response latencies.');
        break;
      case 'REFILL_CREDITS':
        setSearchCredits(prev => prev + 150);
        onAddLog('Galaxy Lab Registry', 'Added 150 search credits via lab quick action', 'SUCCESS', 'Charged to direct-billing records schemes.');
        break;
      case 'CALIBRATE_NODE':
        setIsCalibrating(true);
        onAddLog('Galaxy Lab Calibrator', 'Initiated Node-12 latency calibration sequence', 'PENDING_APPROVAL', 'Running SLA checking algorithms...');
        setTimeout(() => {
          setIsCalibrating(false);
          const randSLA = (0.7 + Math.random() * 0.2).toFixed(2);
          onAddLog('Galaxy Lab Calibrator', 'SLA calibration completed successfully', 'SUCCESS', `Node-12 synchronized in ${randSLA}s standard.`);
        }, 1500);
        break;
      case 'SPAWN_NOTEBOOK':
        const newNbId = `nb-sp-${Date.now()}`;
        const newNb = {
          id: newNbId,
          title: `Autonomous Pitch Draft: ${searchQuery || 'Secured Invoicing Asset'}`,
          sources: [
            { type: 'text', text: 'Auto-ingested via quick action core template generator.' },
            { type: 'link', text: 'https://nexoraxdigital.co.uk/standards' }
          ],
          editorContent: '## Strategic Value Proposition\nThis premium proposal leverages automated Google Workspace tools and zero-fund billing records scheme to absorb recruitment friction.\n\n### Requirements:\n- Immediate client contact via verified phone frequency.\n- Absolute Google ecosystem safety matrices.',
          aiResponse: 'Notebook spawned with 2 integrated sources ready for analysis.'
        };
        setNotebooks(prev => [newNb, ...prev]);
        setActiveNotebookId(newNbId);
        setNotebooksCount(prev => prev + 1);
        onAddLog('Galaxy Notebook', 'Spawned pitch draft notebook via quick action trigger', 'SUCCESS', `Created notebooks catalog entry ID ${newNbId}.`);
        setActiveScreen('notebook');
        break;
      case 'RESET_DEMO':
        setLabSearchQuery('');
        setLabCategoryFilter('ALL');
        setLabSortBy('name');
        setLabSortDir('asc');
        onAddLog('Galaxy Lab Command', 'Reset search, sort and filter filters to core states.', 'SUCCESS', 'Clean slate initialized.');
        break;
      default:
        break;
    }
  };

  // Perform multi-dimensional client-side filtering and sorting on current modules list
  const filteredModules = labModules
    .filter(mod => {
      const query = labSearchQuery.toLowerCase();
      const matchesSearch = mod.name.toLowerCase().includes(query) || 
                            mod.desc.toLowerCase().includes(query) || 
                            mod.category.toLowerCase().includes(query) ||
                            mod.tier.toLowerCase().includes(query) ||
                            mod.status.toLowerCase().includes(query);
      
      const matchesCategory = labCategoryFilter === 'ALL' || mod.category === labCategoryFilter;
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => {
      let comparison = 0;
      if (labSortBy === 'name') {
        comparison = a.name.localeCompare(b.name);
      } else if (labSortBy === 'tier') {
        comparison = a.tier.localeCompare(b.tier);
      } else if (labSortBy === 'status') {
        comparison = a.status.localeCompare(b.status);
      }
      return labSortDir === 'asc' ? comparison : -comparison;
    });

  // Soft upgrade message
  const triggerLimitCheck = () => {
    if (aiTasksRemaining <= 0) {
      setShowLimitWarning(true);
      return false;
    }
    return true;
  };

  const handleActionSpend = () => {
    if (!triggerLimitCheck()) return false;
    setAiTasksRemaining(prev => Math.max(0, prev - 1));
    return true;
  };

  // Run searches with proper validation limits
  const handleRunSearch = () => {
    if (searchQuery.trim() === '') return;
    if (searchCredits <= 0) {
      setShowLimitWarning(true);
      onAddLog('Research Lab', 'Web search attempted but failed due to zero credits', 'WARNING', '0 remaining research search points');
      return;
    }
    setSearchCredits(prev => Math.max(0, prev - 1));
    setSearchHistory(prev => [searchQuery, ...prev]);

    // High fidelity simulated search results
    const results = [
      { id: 'r1', title: `${searchQuery} - Premium Solution Overview`, url: `https://nexus-search.co/item-1`, snippet: `Explore deep automated structures for ${searchQuery}. High velocity integration matching premium standards.` },
      { id: 'r2', title: `Top 10 competitive advantages of ${searchQuery} in modern markets`, url: `https://nexus-search.co/item-2`, snippet: `Analysis of logistics, price matrix, and zero friction transaction systems regarding ${searchQuery}.` },
      { id: 'r3', title: `NexoraX guide to deploying ${searchQuery} on Node structures`, url: `https://nexus-search.co/item-3`, snippet: `Step by step tutorial to setup custom docker environment, environment variable binding, and continuous delivery.` }
    ];
    setSearchResults(results);
    onAddLog('Research Lab', `Executed search: "${searchQuery}"`, 'SUCCESS', 'Search completed successfully, charged 1 credit.');
  };

  // Run App generation simulator
  const handleGenerateApp = () => {
    if (!handleActionSpend()) return;
    
    setGeneratedAppStructure({
      id: 'app-proj-' + Date.now(),
      name: appIdea || 'Everyday Micro App',
      template: selectedAppTemplate,
      structure: [
        { name: 'Header Section', details: 'Full width sticky transparent menu with gold layout accents & dynamic logo rendering' },
        { name: 'Core Feature Container', details: 'A pricing slider and live checkout state integration matching the Wednesday deadline' },
        { name: 'Secure Form Hub', details: 'Direct response input gathering parameters with secure backend storage configuration' },
        { name: 'Footer Section', details: 'Institutional support and legal compliance guidelines linking support@nexoraxdigital.co.uk' }
      ],
      componentList: ['StickyHeader.tsx', 'PricingSlider.tsx', 'StripeForm.tsx', 'InteractiveTicker.tsx', 'NexoraXFooter.tsx'],
      seoMetadata: {
        title: `${appIdea || 'Micro App'} - Predefined Best Performance Checkout`,
        description: `Get real-time secure transactional features. Securely built using NexoraX Node mechanics.`
      },
      hostingRecommendation: 'Nexus High Velocity Cloud Inflow Container'
    });
    
    setSavedProjectsCount(prev => prev + 1);
    onAddLog('App Builder Canvas', `Generated structure for: "${appIdea}"`, 'SUCCESS', 'App structure files built successfully');
  };

  return (
    <div id="nexus-galaxy-lab" className="min-h-screen bg-[#07050e] text-slate-100 font-sans relative overflow-hidden pb-12">
      {/* Dynamic Theme Styles Override Injection */}
      {labTheme === 'MINIMALIST_LIGHT' && (
        <style dangerouslySetInnerHTML={{ __html: `
          #nexus-galaxy-lab {
            --bg-canvas: #faf9f6 !important;
            --text-heading: #1c1917 !important;
            --text-body: #44403c !important;
            --text-muted: #78716c !important;
            --card-bg: #ffffff !important;
            --card-border: #e7e5e4 !important;
            --input-bg: #f5f5f4 !important;
            --input-border: #d6d3d1 !important;
            --input-text: #1c1917 !important;
            background-color: var(--bg-canvas) !important;
            color: var(--text-body) !important;
          }
          
          #nexus-galaxy-lab .bg-zinc-950,
          #nexus-galaxy-lab .bg-zinc-950\\/80,
          #nexus-galaxy-lab .bg-zinc-950\\/60,
          #nexus-galaxy-lab .bg-zinc-950\\/90,
          #nexus-galaxy-lab .bg-zinc-900,
          #nexus-galaxy-lab .bg-black,
          #nexus-galaxy-lab .bg-black\\/60,
          #nexus-galaxy-lab .bg-\\[\\#0b0813\\]\\/85,
          #nexus-galaxy-lab .bg-\\[\\#04020a\\]\\/90,
          #nexus-galaxy-lab .bg-purple-950\\/10,
          #nexus-galaxy-lab .bg-purple-950\\/40,
          #nexus-galaxy-lab .bg-purple-900\\/30,
          #nexus-galaxy-lab .bg-cyan-950\\/40 {
            background-color: var(--card-bg) !important;
            border-color: var(--card-border) !important;
            color: var(--text-body) !important;
          }
          
          #nexus-galaxy-lab .bg-zinc-950:hover,
          #nexus-galaxy-lab .bg-zinc-900:hover,
          #nexus-galaxy-lab .hover\\:bg-zinc-900:hover,
          #nexus-galaxy-lab .hover\\:bg-zinc-900\\/40:hover {
            background-color: #f5f5f4 !important;
          }
          
          #nexus-galaxy-lab .text-white,
          #nexus-galaxy-lab .text-slate-300,
          #nexus-galaxy-lab .text-slate-200,
          #nexus-galaxy-lab .text-slate-100,
          #nexus-galaxy-lab h1,
          #nexus-galaxy-lab h3,
          #nexus-galaxy-lab h4,
          #nexus-galaxy-lab strong,
          #nexus-galaxy-lab input,
          #nexus-galaxy-lab textarea,
          #nexus-galaxy-lab select {
            color: var(--text-heading) !important;
          }
          
          #nexus-galaxy-lab .text-slate-400,
          #nexus-galaxy-lab .text-slate-500,
          #nexus-galaxy-lab .text-slate-450 {
            color: var(--text-muted) !important;
          }
          
          #nexus-galaxy-lab input,
          #nexus-galaxy-lab textarea,
          #nexus-galaxy-lab select {
            background-color: var(--input-bg) !important;
            border-color: var(--input-border) !important;
            color: var(--input-text) !important;
          }
          
          #nexus-galaxy-lab .border-zinc-805,
          #nexus-galaxy-lab .border-zinc-800,
          #nexus-galaxy-lab .border-zinc-850,
          #nexus-galaxy-lab .border-zinc-900,
          #nexus-galaxy-lab .border-purple-500\\/20,
          #nexus-galaxy-lab .border-zinc-700,
          #nexus-galaxy-lab .border-purple-600\\/30 {
            border-color: var(--card-border) !important;
          }
          
          #nexus-galaxy-lab .bg-gradient-to-r,
          #nexus-galaxy-lab .bg-purple-950 {
            background-color: #f3e8ff !important;
            color: #6b21a8 !important;
            border-color: #d8b4fe !important;
          }
          #nexus-galaxy-lab .text-purple-400 {
            color: #7e22ce !important;
          }
          #nexus-galaxy-lab .text-cyan-400 {
            color: #0369a1 !important;
          }
          #nexus-galaxy-lab .text-amber-400 {
            color: #b45309 !important;
          }
          
          #nexus-galaxy-lab .bg-amber-950\\/30 {
            background-color: #fef3c7 !important;
            color: #b45309 !important;
            border-color: #fde68a !important;
          }
          
          #nexus-galaxy-lab #galaxy-lab-modules-tabs button {
            background-color: #f5f5f4 !important;
            border-color: #e7e5e4 !important;
            color: #44403c !important;
          }
          
          #nexus-galaxy-lab #galaxy-lab-modules-tabs button:hover {
            background-color: #e7e5e4 !important;
          }
          
          #nexus-galaxy-lab #galaxy-lab-modules-tabs button[class*="bg-purple-950"] {
            background-color: #f3e8ff !important;
            border-color: #c084fc !important;
            color: #6b21a8 !important;
          }
          
          #nexus-galaxy-lab svg {
            stroke: currentColor;
          }
        `}} />
      )}

      {/* Background radial effects imitating Galaxy Workspace details */}
      <div className="absolute top-10 left-10 w-96 h-96 bg-purple-900/10 rounded-full blur-[100px] pointer-events-none"></div>
      <div className="absolute bottom-20 right-10 w-[450px] h-[450px] bg-cyan-950/10 rounded-full blur-[120px] pointer-events-none"></div>
      <div className="absolute top-[40%] left-[30%] w-72 h-72 bg-amber-500/5 rounded-full blur-[90px] pointer-events-none"></div>

      {/* Main Container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10 pt-4">
        
        {/* Header Breadcrumb Banner */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between border-b border-purple-500/20 pb-4 mb-6">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="text-[10px] bg-purple-950 text-purple-400 border border-purple-600/30 font-mono font-black uppercase tracking-widest px-2.5 py-0.5 rounded-md">
                Module Node-12
              </span>
              <span className="text-[10px] bg-cyan-950 text-cyan-400 border border-cyan-400/30 font-mono font-black uppercase tracking-widest px-2.5 py-0.5 rounded-md flex items-center gap-1">
                <Sparkles className="w-3 h-3 text-cyan-400" /> Premium Workspace
              </span>
              <span className="text-[10px] bg-amber-950 text-amber-400 border border-amber-400/30 font-mono font-black uppercase tracking-widest px-2.5 py-0.5 rounded-md">
                Active Client Access
              </span>
            </div>
            <h1 className="text-3xl md:text-4xl font-sans font-black tracking-tight text-white uppercase mt-1">
              Nexus Galaxy Lab
            </h1>
            <p className="text-xs font-mono tracking-wider text-slate-400">
              Research. Build. Automate. Create. Publish.
            </p>
          </div>

          {/* Quick Metrics display */}
          <div className="flex flex-wrap gap-2 mt-4 md:mt-0 font-mono text-xs items-center">
            {/* Theme Toggle Button */}
            <button 
              onClick={toggleLabTheme}
              className={`border rounded-xl px-3 py-2 cursor-pointer transition-all flex items-center gap-1.5 font-bold ${
                labTheme === 'RESEARCH_DARK' 
                  ? 'bg-zinc-950 border-zinc-800 text-slate-300 hover:bg-zinc-900 hover:text-white'
                  : 'bg-white border-stone-200 text-stone-850 hover:bg-stone-50'
              }`}
              title="Toggle Research Dark or Minimalist Light Mode"
            >
              {labTheme === 'RESEARCH_DARK' ? (
                <>
                  <span className="text-amber-400">☀️</span>
                  <span>Minimalist Light</span>
                </>
              ) : (
                <>
                  <span className="text-indigo-400">🌌</span>
                  <span>Research Dark</span>
                </>
              )}
            </button>

            <div className={`border rounded-xl px-3 py-2 flex items-center gap-2 ${
              labTheme === 'RESEARCH_DARK' ? 'bg-zinc-950/80 border-zinc-800' : 'bg-white border-stone-200 text-stone-700'
            }`}>
              <Search className="w-3.5 h-3.5 text-cyan-400" />
              <span>Search Credits: <strong className="text-cyan-450">{searchCredits}</strong></span>
            </div>
            <div className={`border rounded-xl px-3 py-2 flex items-center gap-2 ${
              labTheme === 'RESEARCH_DARK' ? 'bg-zinc-950/80 border-zinc-800' : 'bg-white border-stone-200 text-stone-700'
            }`}>
              <Cpu className="w-3.5 h-3.5 text-purple-400" />
              <span>AI Tasks Remaining: <strong className="text-purple-400">{aiTasksRemaining}</strong></span>
            </div>
            <button 
              onClick={() => setActiveScreen('community')}
              className="bg-amber-950/30 hover:bg-amber-950/50 border border-amber-500/20 rounded-xl px-3 py-2 text-amber-400 cursor-pointer transition-all flex items-center gap-1"
            >
              <Heart className="w-3.5 h-3.5 fill-amber-400/20 text-amber-400" />
              <span>Community Impact</span>
            </button>
          </div>
        </div>

        {/* SOFT LIMIT WARNING ALERT PANEL */}
        {showLimitWarning && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="mb-6 rounded-2xl bg-amber-950/30 border-2 border-amber-500/50 p-4 shadow-[0_0_20px_rgba(245,158,11,0.15)] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
          >
            <div className="flex items-center gap-3">
              <AlertTriangle className="text-amber-400 shrink-0 w-6 h-6" />
              <div>
                <h4 className="text-sm font-bold text-amber-200">Usage Recommendation Check Triggered</h4>
                <p className="text-xs text-slate-300">You have reached the limit. Upgrade, buy credits, or continue with free tools next month.</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button 
                onClick={() => {
                  setAiTasksRemaining(35);
                  setSearchCredits(500);
                  setShowLimitWarning(false);
                  onAddLog('Billing', 'User triggered soft simulator credits refill', 'SUCCESS', 'Plan boosted to accommodate new tasks');
                }}
                className="bg-gradient-to-r from-amber-500 to-yellow-600 hover:scale-[1.02] text-black text-[11px] font-mono font-black px-4 py-2 rounded-xl transition-all uppercase"
              >
                Simulate Credit Upgrade
              </button>
              <button 
                onClick={() => setShowLimitWarning(false)}
                className="text-slate-400 hover:text-white px-3 py-2 text-xs"
              >
                Dismiss
              </button>
            </div>
          </motion.div>
        )}

        {/* OVERALL TAB SELECTOR SLIDER BAR */}
        <div id="galaxy-lab-modules-tabs" className="flex overflow-x-auto gap-2 pb-2 mb-6 scrollbar-thin scrollbar-thumb-zinc-800 text-xs font-mono">
          <button
            onClick={() => setActiveScreen('home')}
            className={`px-3 py-2 rounded-xl border transition-all whitespace-nowrap cursor-pointer ${
              activeScreen === 'home' 
              ? 'bg-purple-950/40 text-purple-400 border-purple-500/50 shadow-[0_0_10px_rgba(168,85,247,0.2)]' 
              : 'bg-zinc-950/60 text-slate-400 border-zinc-800 hover:bg-zinc-900/60'
            }`}
          >
            🏠 Home Dashboard
          </button>
          {[
            { id: 'product_factory', name: '🏭 AI Product Factory' },
            { id: 'notebook', name: '📚 Galaxy Notebook' },
            { id: 'prompt', name: '⚡ Prompt Studio' },
            { id: 'agent', name: '🤖 Agent Mission' },
            { id: 'app', name: '💻 App Builder' },
            { id: 'flow', name: '🔗 Automation Flows' },
            { id: 'search', name: '🔍 Research Lab' },
            { id: 'code', name: '💽 Code Workspace' },
            { id: 'media', name: '🎥 Media Dock' },
            { id: 'worksuite', name: '📋 WorkSuite PM' },
            { id: 'publish', name: '🌐 Publish Hub' },
            { id: 'learning', name: '📖 Template Library' },
            { id: 'community', name: '💝 Community Impact' }
          ].map((sc) => (
            <button
              key={sc.id}
              onClick={() => setActiveScreen(sc.id)}
              className={`px-3 py-2 rounded-xl border transition-all whitespace-nowrap cursor-pointer ${
                activeScreen === sc.id 
                ? 'bg-[#00ffff]/10 text-[#00ffff] border-[#00ffff]/40 shadow-[0_0_10px_rgba(0,255,255,0.15)]' 
                : 'bg-zinc-950/60 text-slate-400 border-zinc-800 hover:bg-zinc-900/60'
              }`}
            >
              {sc.name}
            </button>
          ))}
        </div>

        {/* SCREEN PAGES INTERACTION PANELS */}
        <div id="galaxy-lab-screen-viewport" className="min-h-[500px]">
          
          {/* ==================== SCREEN 1: GALAXY LAB HOME ==================== */}
          {activeScreen === 'home' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
              {/* Hero Banner Grid */}
              <div className="p-8 md:p-12 rounded-3xl bg-gradient-to-br from-purple-950/20 via-black/90 to-zinc-950 border border-purple-500/20 relative overflow-hidden shadow-[0_0_40px_rgba(168,85,247,0.15)]">
                <div className="absolute top-0 right-0 w-64 h-64 bg-cyan-500/10 rounded-full blur-[80px] pointer-events-none"></div>
                
                <div className="max-w-3xl relative z-10 text-left">
                  <span className="text-amber-400 text-xs font-mono font-black tracking-widest uppercase block mb-2">
                    Build with AI. Learn with AI. Launch with Nexus.
                  </span>
                  <h2 className="text-3xl md:text-5xl font-sans font-black tracking-tight text-white uppercase leading-none mb-4">
                    Your Autonomous AI Workspace <br />
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-purple-400 to-amber-400">for Ideas & Code</span>
                  </h2>
                  <p className="text-slate-300 text-sm md:text-base leading-relaxed max-w-2xl font-normal">
                    Turn loose concepts into comprehensive notebooks, automated workflow chains, robust pricing calculators, multi-screen landing draft assets, and high velocity creative media — fully aligned to the active NexoraX ecosystem.
                  </p>

                  {/* Immediate Action Buttons */}
                  <div className="flex flex-wrap gap-3 mt-8">
                    <button 
                      onClick={() => setActiveScreen('notebook')}
                      className="px-5 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-mono text-[11px] font-black uppercase rounded-xl tracking-wider hover:scale-[1.03] transition-all cursor-pointer shadow-[0_0_15px_rgba(147,51,234,0.3)]"
                    >
                      Open Notebook
                    </button>
                    <button 
                      onClick={() => setActiveScreen('app')}
                      className="px-5 py-3 bg-gradient-to-r from-cyan-500 to-teal-500 text-black font-mono text-[11px] font-black uppercase rounded-xl tracking-wider hover:scale-[1.03] transition-all cursor-pointer shadow-[0_0_15px_rgba(6,182,212,0.3)]"
                    >
                      Build an App
                    </button>
                    <button 
                      onClick={() => setActiveScreen('search')}
                      className="px-5 py-3 bg-zinc-900 border border-zinc-700 hover:bg-zinc-800 text-slate-100 font-mono text-[11px] font-black uppercase rounded-xl tracking-wider cursor-pointer transition-all"
                    >
                      Run Nexus Search
                    </button>
                    <button 
                      onClick={() => setActiveScreen('agent')}
                      className="px-5 py-3 bg-amber-500 hover:bg-amber-600 text-black font-mono text-[11px] font-black uppercase rounded-xl tracking-wider cursor-pointer transition-all hover:scale-[1.02]"
                    >
                      Deploy Agent
                    </button>
                    <button 
                      onClick={() => setActiveScreen('media')}
                      className="px-4 py-3 bg-indigo-950/40 text-purple-300 border border-purple-500/20 hover:bg-indigo-950/60 text-[11px] font-mono font-black uppercase rounded-xl cursor-pointer"
                    >
                      Create Media
                    </button>
                    <button 
                      onClick={() => setActiveScreen('publish')}
                      className="px-4 py-3 bg-cyan-950/40 text-cyan-300 border border-cyan-500/20 hover:bg-cyan-950/60 text-[11px] font-mono font-black uppercase rounded-xl cursor-pointer"
                    >
                      Publish Project
                    </button>
                  </div>
                </div>
              </div>

              {/* MISSION STATEMENT ACCORDION CARD / HIGHLIGHT WIDGET */}
              <div className="bg-gradient-to-r from-indigo-950/20 to-purple-950/20 border border-purple-500/20 rounded-2xl p-6 text-left">
                <div className="flex gap-4 items-start">
                  <div className="bg-amber-500/10 border border-amber-500/30 text-amber-400 p-2.5 rounded-xl shrink-0">
                    <Heart className="w-5 h-5 fill-amber-400/20" />
                  </div>
                  <div className="space-y-1">
                    <h4 className="text-sm font-mono font-black uppercase text-amber-400 tracking-wider">Mission Statement</h4>
                    <p className="text-sm font-bold text-white">AI tools for everyday builders, creators, students, founders, charities, and small businesses.</p>
                    <p className="text-sm text-slate-400 leading-relaxed font-sans mt-1">
                      Nexus Galaxy Lab helps people learn, build, automate, and publish without needing complex technical skills. Optional paid credits, hosting, and premium upgrades help sustain the platform and may support nominated community or charitable initiatives where enabled.
                    </p>
                  </div>
                </div>
              </div>

              {/* SAAS PLATFORM PREVIEW & TIER INTEGRATION CARD */}
              <div className="bg-gradient-to-r from-purple-950/20 via-[#030612]/40 to-cyan-950/20 border border-cyan-500/20 rounded-2xl p-6 text-left space-y-4">
                <div className="flex gap-4 items-start border-b border-zinc-900 pb-4">
                  <div className="bg-purple-500/10 border border-purple-500/30 text-purple-400 p-2.5 rounded-xl shrink-0">
                    <Sparkles className="w-5 h-5 text-purple-400" />
                  </div>
                  <div className="space-y-1">
                    <h4 className="text-sm font-mono font-black uppercase text-purple-200 tracking-wider">A  creative AI workspace</h4>
                    <p className="text-sm font-bold text-white">Nexus Galaxy Lab is a creative AI workspace designed for frictionless exploration.</p>
                    <p className="text-xs text-slate-455 leading-relaxed font-sans mt-1">
                      Build a , easy-to-use AI workspace directly connected to your active billing tiers and limits. Live client assets are preserved in local sessions.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs font-mono">
                  <div className="p-4 bg-black/45 border border-zinc-850 rounded-xl space-y-2">
                    <div className="text-white font-extrabold text-[10px] uppercase">Create a  Galaxy Account tier</div>
                    <p className="text-slate-450 text-[10px] leading-relaxed">
                      Register to unlock unlimited file snapshots. Without active plans, users get limited monthly searches across the wider Web.
                    </p>
                  </div>

                  <div className="p-4 bg-black/45 border border-zinc-850 rounded-xl space-y-2">
                    <div className="text-cyan-400 font-extrabold text-[10px] uppercase">Start  Galaxy Lab  payment tiers</div>
                    <p className="text-slate-450 text-[10px] leading-relaxed">
                      Start  Galaxy Lab  payment tiers to sync with standard Bronze, Silver, or Gold sub-matrices. All secure queries and token meters update instantly.
                    </p>
                  </div>
                </div>
              </div>

              {/* KPI Status Tracker Boxes */}
              <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                {[
                  { title: 'Free Searches', val: searchCredits, icon: Search, color: 'text-cyan-400' },
                  { title: 'AI Tasks', val: aiTasksRemaining, icon: Cpu, color: 'text-purple-400' },
                  { title: 'Active Notebooks', val: notebooksCount, icon: BookOpen, color: 'text-blue-400' },
                  { title: 'Projects Saved', val: savedProjectsCount, icon: FolderGit, color: 'text-amber-400' },
                  { title: 'Community Fund', val: charityConfig.publicDisclosureEnabled ? '3% Allocated' : 'Incomplete Config', icon: Heart, color: 'text-rose-400' },
                  { title: 'Priority Status', val: user.plan, icon: Zap, color: 'text-yellow-400' }
                ].map((kpi, i) => (
                  <div key={i} className="bg-zinc-950/90 border border-zinc-800/80 rounded-2xl p-4 text-center space-y-1 hover:border-zinc-700 transition-all">
                    <div className="flex justify-center mb-1">
                      <kpi.icon className={`w-5 h-5 ${kpi.color}`} />
                    </div>
                    <span className="block text-[10px] text-slate-500 font-mono uppercase tracking-wider">{kpi.title}</span>
                    <strong className="block text-sm md:text-lg text-white font-mono">{kpi.val}</strong>
                  </div>
                ))}
              </div>

              {/* --- LAB QUICK ACTIONS PANEL --- */}
              <div className="bg-[#0b0813]/85 border border-purple-500/20 rounded-3xl p-6 text-left space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-zinc-900 pb-3">
                  <div>
                    <h4 className="text-sm font-mono font-black uppercase text-purple-300 tracking-wider flex items-center gap-1.5">
                      <Zap className="w-4 h-4 text-purple-400 animate-pulse" /> Lab Quick Utility Actions
                    </h4>
                    <p className="text-slate-400 text-xs mt-0.5">Simulate system-level node triggers and auto-configure sandbox profiles.</p>
                  </div>
                  {isCalibrating && (
                    <span className="text-[10px] bg-amber-500/10 border border-amber-500/30 text-amber-400 px-2.5 py-1 rounded-xl font-mono animate-pulse flex items-center gap-1">
                      <RefreshCw className="w-3 h-3 animate-spin" /> Node Calibration SLA active...
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  <button
                    onClick={() => handleLabQuickAction('DIAGNOSTIC')}
                    className="p-3 bg-zinc-950 hover:bg-zinc-900 border border-zinc-850 rounded-xl text-left hover:border-purple-500/40 hover:-translate-y-0.5 transition-all cursor-pointer group"
                  >
                    <div className="text-[10px] font-mono text-purple-400 font-extrabold uppercase tracking-wide">Test Diagnostic</div>
                    <p className="text-slate-500 text-[9px] font-sans mt-1 group-hover:text-slate-300">Run Node system health ping checks</p>
                  </button>

                  <button
                    onClick={() => handleLabQuickAction('REFILL_CREDITS')}
                    className="p-3 bg-zinc-950 hover:bg-zinc-900 border border-zinc-850 rounded-xl text-left hover:border-cyan-500/40 hover:-translate-y-0.5 transition-all cursor-pointer group"
                  >
                    <div className="text-[10px] font-mono text-cyan-400 font-extrabold uppercase tracking-wide">+150 Credits</div>
                    <p className="text-slate-500 text-[9px] font-sans mt-1 group-hover:text-slate-300">Bulk credit injection scheme refill</p>
                  </button>

                  <button
                    onClick={() => handleLabQuickAction('CALIBRATE_NODE')}
                    disabled={isCalibrating}
                    className="p-3 bg-zinc-950 hover:bg-zinc-900 border border-zinc-850 rounded-xl text-left hover:border-amber-500/40 hover:-translate-y-0.5 transition-all cursor-pointer group disabled:opacity-50"
                  >
                    <div className="text-[10px] font-mono text-amber-400 font-extrabold uppercase tracking-wide">Calibrate Link</div>
                    <p className="text-slate-500 text-[9px] font-sans mt-1 group-hover:text-slate-300">Tune Node-12 response latencies</p>
                  </button>

                  <button
                    onClick={() => handleLabQuickAction('SPAWN_NOTEBOOK')}
                    className="p-3 bg-zinc-950 hover:bg-zinc-950/40 border border-zinc-850 rounded-xl text-left hover:border-emerald-500/40 hover:-translate-y-0.5 transition-all cursor-pointer group"
                  >
                    <div className="text-[10px] font-mono text-emerald-400 font-extrabold uppercase tracking-wide">Spawn Notebook</div>
                    <p className="text-slate-500 text-[9px] font-sans mt-1 group-hover:text-slate-300">Generate autonomous sales-pitch note</p>
                  </button>

                  <button
                    onClick={() => handleLabQuickAction('RESET_DEMO')}
                    className="p-3 bg-zinc-950 hover:bg-zinc-900 border border-zinc-850 rounded-xl text-left hover:border-rose-500/40 hover:-translate-y-0.5 transition-all cursor-pointer group col-span-2 md:col-span-1"
                  >
                    <div className="text-[10px] font-mono text-rose-450 font-extrabold uppercase tracking-wide">Reset Filters</div>
                    <p className="text-slate-500 text-[9px] font-sans mt-1 group-hover:text-slate-300">Clear search terms and sorting</p>
                  </button>
                </div>

                {/* Optional Expandable Diagnostic Telemetry Window */}
                {diagnosticLogs.length > 0 && (
                  <div className="mt-4 bg-black border border-zinc-900 rounded-2xl p-4 text-left">
                    <div className="flex items-center justify-between border-b border-zinc-900 pb-2 mb-2">
                      <span className="text-[10px] font-mono text-purple-400 uppercase font-black">Diagnostic Inbound Console Log</span>
                      <button 
                        onClick={() => setDiagnosticLogs([])}
                        className="text-[9px] font-mono text-slate-500 hover:text-slate-200"
                      >
                        Clear console screen
                      </button>
                    </div>
                    <div className="max-h-24 overflow-y-auto space-y-1 scrollbar-thin text-[10px] font-mono text-slate-400">
                      {diagnosticLogs.map((log, index) => (
                        <div key={index} className="leading-relaxed">{log}</div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* --- LABORATORY SEARCH, FILTER, AND SORT PANEL --- */}
              <div className="bg-[#04020a]/90 border border-zinc-800 rounded-3xl p-6 text-left space-y-4">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                  {/* Search input */}
                  <div className="lg:col-span-5 relative">
                    <span className="block text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-1.5">Laboratory Search</span>
                    <div className="relative">
                      <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                      <input
                        type="text"
                        value={labSearchQuery}
                        onChange={(e) => setLabSearchQuery(e.target.value)}
                        placeholder="Search model, feature name, tier, or status..."
                        className="w-full bg-black border border-zinc-800/80 rounded-xl pl-10 pr-4 py-2.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-purple-500/50"
                      />
                      {labSearchQuery && (
                        <button
                          onClick={() => setLabSearchQuery('')}
                          className="absolute right-3.5 top-1/2 -translate-y-1/2 text-xs font-mono text-slate-500 hover:text-slate-200"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Filter category selector */}
                  <div className="lg:col-span-4">
                    <span className="block text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-1.5">Filter by Category</span>
                    <select
                      value={labCategoryFilter}
                      onChange={(e) => setLabCategoryFilter(e.target.value)}
                      className="w-full bg-black border border-zinc-800/80 rounded-xl px-3 py-2.5 text-xs text-slate-200 focus:outline-none focus:border-purple-500/50"
                    >
                      <option value="ALL">All Categories</option>
                      <option value="Notebooks">Notebooks</option>
                      <option value="Core Developer">Core Developer</option>
                      <option value="Autonomous Agents">Autonomous Agents</option>
                      <option value="Design/Builder">Design/Builder</option>
                      <option value="Automated Operations">Automated Operations</option>
                      <option value="Information Auditing">Information Auditing</option>
                      <option value="Media & Copywriting">Media & Copywriting</option>
                      <option value="Ecosystem Deployment">Ecosystem Deployment</option>
                    </select>
                  </div>

                  {/* Sorting controls */}
                  <div className="lg:col-span-3">
                    <span className="block text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-1.5">Sort Matrix</span>
                    <div className="flex gap-2">
                      <select
                        value={labSortBy}
                        onChange={(e) => setLabSortBy(e.target.value as any)}
                        className="flex-1 bg-black border border-zinc-800/80 rounded-xl px-3 py-2.5 text-xs text-slate-200 focus:outline-none focus:border-purple-500/50"
                      >
                        <option value="name">Module Name</option>
                        <option value="tier">Access Tier</option>
                        <option value="status">Status</option>
                      </select>
                      <button
                        onClick={() => setLabSortDir(prev => prev === 'asc' ? 'desc' : 'asc')}
                        className="bg-black hover:bg-zinc-950 border border-zinc-800/80 p-2.5 rounded-xl text-slate-400 hover:text-white transition-all text-xs font-mono font-bold"
                        title={labSortDir === 'asc' ? 'Sort Ascending' : 'Sort Descending'}
                      >
                        {labSortDir === 'asc' ? '▲ ASC' : '▼ DESC'}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Instant Category badging selector strip */}
                <div className="flex flex-wrap gap-1.5 pt-1.5">
                  <span className="text-[9px] font-mono text-slate-500 uppercase flex items-center pr-1.5">Quick Filters:</span>
                  {['ALL', 'Notebooks', 'Core Developer', 'Autonomous Agents', 'Design/Builder', 'Automated Operations'].map((cat) => (
                    <button
                      key={cat}
                      onClick={() => setLabCategoryFilter(cat)}
                      className={`px-3 py-1 text-[9px] font-mono rounded-lg transition-all ${
                        labCategoryFilter === cat
                          ? 'bg-purple-900/35 border border-purple-500 text-purple-200 shadow-md'
                          : 'bg-black border border-zinc-850 text-slate-400 hover:border-zinc-700 hover:text-slate-200'
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>

              {/* Main Feature grid of Galaxy Lab */}
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-left">
                  <h3 className="text-md font-mono font-black uppercase text-slate-300 tracking-wider">
                    Explore {filteredModules.length} Matching Lab Modules
                  </h3>
                  {labSearchQuery || labCategoryFilter !== 'ALL' ? (
                    <span className="text-[10px] font-mono text-[#00ffff] bg-[#00ffff]/5 border border-[#00ffff]/20 px-2.5 py-1 rounded-xl">
                      Active Filters applied
                    </span>
                  ) : null}
                </div>

                {filteredModules.length === 0 ? (
                  <div className="p-12 text-center bg-zinc-950/80 border border-dashed border-zinc-800 rounded-3xl space-y-2">
                    <Sliders className="w-8 h-8 text-slate-600 mx-auto" />
                    <h4 className="text-sm font-mono text-slate-400 font-bold">No Modules Found Matching Search Filters</h4>
                    <p className="text-xs text-slate-500 max-w-sm mx-auto">Try lowering category restrictions, adjusting the search term keyword, or resetting parameters back to default system layout presets.</p>
                    <button
                      onClick={() => handleLabQuickAction('RESET_DEMO')}
                      className="px-4 py-2 mt-2 bg-purple-950/30 border border-purple-500/20 text-purple-400 font-mono text-[10px] font-bold uppercase rounded-lg hover:bg-purple-950/60"
                    >
                      Reset Filter Criteria
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {filteredModules.map((feat) => {
                      const IconComponent = feat.icon;
                      return (
                        <div 
                          key={feat.id} 
                          onClick={() => setActiveScreen(feat.id)}
                          className={`p-6 rounded-2xl bg-zinc-950/80 border ${feat.accent} cursor-pointer transition-all text-left space-y-3 relative group hover:bg-zinc-900/40`}
                        >
                          {/* Tier badge indicators */}
                          <div className="absolute top-4 right-4 flex items-center gap-1.5">
                            <span className={`text-[8px] font-mono font-bold uppercase tracking-widest px-2 py-0.5 rounded border ${
                              feat.tier === 'Free' ? 'bg-zinc-900 border-zinc-800 text-slate-400' :
                              feat.tier === 'Pro' ? 'bg-purple-950/40 border-purple-500/30 text-purple-300' :
                              feat.tier === 'Bronze' ? 'bg-amber-950/40 border-amber-500/20 text-amber-400' :
                              feat.tier === 'Silver' ? 'bg-indigo-950/40 border-indigo-500/20 text-indigo-400' :
                              feat.tier === 'Gold' ? 'bg-yellow-950/40 border-yellow-500/20 text-yellow-300 animate-pulse' :
                              'bg-cyan-950/40 border-cyan-500/20 text-cyan-400'
                            }`}>
                              {feat.tier}
                            </span>
                            <span className={`text-[8px] font-mono font-bold px-1.5 py-0.5 rounded border uppercase ${
                              feat.status === 'Ready' ? 'bg-emerald-950/40 border-emerald-500/20 text-emerald-400' :
                              feat.status === 'Calibrated' ? 'bg-cyan-950/40 border-cyan-500/20 text-cyan-400' :
                              feat.status === 'Active' ? 'bg-indigo-950/45 border-indigo-500/20 text-indigo-400' :
                              'bg-amber-950/40 border-amber-500/10 text-amber-300'
                            }`}>
                              {feat.status}
                            </span>
                          </div>

                          <div className="bg-zinc-900 border border-zinc-800 p-2.5 rounded-xl w-fit group-hover:scale-105 transition-all">
                            <IconComponent className="w-5 h-5 text-slate-300 group-hover:text-amber-400 transition-colors" />
                          </div>
                          
                          <div className="space-y-1">
                            <span className="text-[8px] font-mono font-extrabold uppercase bg-black hover:border-zinc-800 border border-zinc-900 text-slate-500 px-1.5 py-0.5 rounded tracking-wide">
                              {feat.category}
                            </span>
                            <h4 className="text-sm font-mono font-black uppercase text-white tracking-wide group-hover:text-amber-400 pt-1">
                              {feat.name}
                            </h4>
                          </div>

                          <p className="text-xs text-slate-400 leading-relaxed font-sans min-h-[48px]">
                            {feat.desc}
                          </p>
                          <div className="pt-2 flex items-center text-[10px] font-mono text-amber-500 uppercase tracking-widest gap-1">
                            <span>Access module</span>
                            <ChevronRight className="w-3 h-3 group-hover:translate-x-1 transition-all" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

            </motion.div>
          )}

          {/* ==================== SCREEN: NEXUS AI PRODUCT FACTORY ==================== */}
          {activeScreen === 'product_factory' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <NexusAIProductFactory
                user={user}
                isAdminMode={isAdminMode}
                onAddLog={onAddLog}
                onAddTaskToWorkSuite={onAddTaskToWorkSuite}
                currentRole={currentRole}
              />
            </motion.div>
          )}

          {/* ==================== SCREEN 2: GALAXY NOTEBOOK ==================== */}
          {activeScreen === 'notebook' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="grid grid-cols-1 lg:grid-cols-12 gap-6 text-left">
              
              {/* Left Panel: Sources */}
              <div className="lg:col-span-3 space-y-4 bg-zinc-950/80 border border-zinc-800 rounded-3xl p-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-mono font-black uppercase tracking-wider text-purple-400">Notebook Sources</h3>
                  <span className="text-[10px] font-mono bg-purple-950 text-purple-400 border border-purple-600/30 px-2 py-0.5 rounded">
                    Active
                  </span>
                </div>

                <div className="space-y-2 mt-4 max-h-[300px] overflow-y-auto">
                  {notebooks.find(n => n.id === activeNotebookId)?.sources.map((src, idx) => (
                    <div key={idx} className="p-3 rounded-xl bg-black border border-zinc-900/80 space-y-1 relative group">
                      <span className="text-[9px] font-mono uppercase bg-zinc-900 text-slate-400 border border-zinc-800/60 px-1.5 py-0.5 rounded tracking-widest">
                        {src.type}
                      </span>
                      <p className="text-xs text-slate-300 font-sans mt-1 line-clamp-3">{src.text}</p>
                    </div>
                  ))}
                </div>

                {/* Adding dynamic Source Inputs */}
                <div className="space-y-2 pt-4 border-t border-zinc-900">
                  <span className="text-[10px] font-mono text-slate-500 uppercase">Load New Source</span>
                  <input 
                    type="text"
                    value={newSourceUrl} 
                    onChange={(e) => setNewSourceUrl(e.target.value)} 
                    placeholder="Enter website link or PDF title"
                    className="w-full bg-black border border-zinc-800 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-purple-500/50"
                  />
                  <button 
                    onClick={() => {
                      if (!newSourceUrl.trim()) return;
                      setNotebooks(prev => prev.map(n => {
                        if (n.id === activeNotebookId) {
                          return {
                            ...n,
                            sources: [...n.sources, { type: 'link', text: newSourceUrl }]
                          };
                        }
                        return n;
                      }));
                      onAddLog('Galaxy Notebook', `Loaded research source note`, 'SUCCESS', 'Custom link parameters logged to active canvas');
                      setNewSourceUrl('');
                    }}
                    className="w-full py-2 bg-purple-900/30 hover:bg-purple-900/50 text-purple-300 rounded-xl text-xs font-mono font-black uppercase tracking-wider transition-all"
                  >
                    Add Website Source
                  </button>
                </div>
              </div>

              {/* Center Panel: Editor */}
              <div className="lg:col-span-5 space-y-4 bg-zinc-950/80 border border-zinc-800 rounded-3xl p-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-mono font-black uppercase text-cyan-400 tracking-wider">Notebook Text Canvas</h3>
                  <BookOpen className="w-4 h-4 text-cyan-500" />
                </div>

                <div className="space-y-3">
                  <input 
                    type="text" 
                    value={notebooks.find(n => n.id === activeNotebookId)?.title}
                    onChange={(e) => {
                      const updatedTitle = e.target.value;
                      setNotebooks(prev => prev.map(n => {
                        if (n.id === activeNotebookId) {
                          return { ...n, title: updatedTitle };
                        }
                        return n;
                      }));
                    }}
                    className="w-full bg-black border-b border-zinc-800 p-2 text-sm font-bold text-white focus:outline-none focus:border-cyan-500"
                  />

                  <textarea 
                    value={notebooks.find(n => n.id === activeNotebookId)?.editorContent}
                    onChange={(e) => {
                      const val = e.target.value;
                      setNotebooks(prev => prev.map(n => {
                        if (n.id === activeNotebookId) {
                          return { ...n, editorContent: val };
                        }
                        return n;
                      }));
                    }}
                    className="w-full h-80 bg-black/60 border border-zinc-900 rounded-2xl p-4 text-xs font-mono text-slate-300 leading-relaxed focus:outline-none focus:border-cyan-400/30"
                  />
                </div>

                {/* Helper action outputs */}
                <div className="flex flex-wrap gap-2 pt-2">
                  <button 
                    onClick={() => {
                      if (!handleActionSpend()) return;
                      onAddLog('Galaxy Notebook', 'Generated research action plan', 'SUCCESS', 'Study guide outputs created');
                      setNotebooks(prev => prev.map(n => {
                        if (n.id === activeNotebookId) {
                          return {
                            ...n,
                            aiResponse: 'Summary: Structured 3-step timeline built. Grounded in listed coffee research sources. FAQ lists: Is direct payment supported? Yes, through live SecurePayment routes.'
                          };
                        }
                        return n;
                      }));
                    }}
                    className="px-3 py-1.5 bg-zinc-900 border border-zinc-700 hover:bg-zinc-800 rounded-lg text-[10px] font-mono tracking-wide uppercase"
                  >
                    ⚡ Study Guide
                  </button>
                  <button 
                    onClick={() => {
                      if (!handleActionSpend()) return;
                      onAddLog('Galaxy Notebook', 'Compiled FAQ draft', 'SUCCESS', 'Questions assembled');
                    }}
                    className="px-3 py-1.5 bg-zinc-900 border border-zinc-700 hover:bg-zinc-800 rounded-lg text-[10px] font-mono tracking-wide uppercase"
                  >
                    ❓ FAQ Lists
                  </button>
                  <button 
                    onClick={() => {
                      // Send to WorkSuite Board
                      const currentNoteb = notebooks.find(n => n.id === activeNotebookId);
                      onAddTaskToWorkSuite({
                        id: 'task-noteb-' + Date.now(),
                        title: `Execute notebook plan: "${currentNoteb?.title}"`,
                        project: 'Galaxy Research',
                        client: 'NexoraX Client Representative',
                        priority: 'LOW',
                        status: 'TODO'
                      });
                      onAddLog('Galaxy Notebook', 'Pushed study draft to WorkSuite PM', 'SUCCESS', 'Active synchronization completed');
                    }}
                    className="px-3 py-1.5 bg-cyan-950/40 text-cyan-400 border border-cyan-500/20 hover:bg-cyan-950/60 rounded-lg text-[10px] font-mono tracking-wide uppercase"
                  >
                    Push to WorkSuite
                  </button>
                </div>
              </div>

              {/* Right Panel: AI Assistant and outputs */}
              <div className="lg:col-span-4 space-y-4 bg-zinc-950/80 border border-zinc-800 rounded-3xl p-6 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-24 h-24 bg-purple-500/5 blur-[40px] rounded-full pointer-events-none"></div>
                
                <h3 className="text-xs font-mono font-black uppercase text-amber-500 tracking-wider">Grounding Inspector Model</h3>

                <div className="space-y-3">
                  <div className="p-4 rounded-2xl bg-purple-950/10 border border-purple-500/10 text-xs leading-relaxed text-slate-300 font-sans min-h-[160px]">
                    {notebooks.find(n => n.id === activeNotebookId)?.aiResponse}
                  </div>

                  {/* Client Grounding Warning Checkbox */}
                  {queryGrounded === false && (
                    <div className="p-3 bg-rose-950/20 border border-rose-500/30 text-rose-300 text-[11px] rounded-xl flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" />
                      <span>Warning: AI response parameters cannot be fully grounded in your custom sources list. Proceed with care.</span>
                    </div>
                  )}

                  <div className="space-y-2 pt-2">
                    <span className="text-[10px] font-mono text-slate-500 uppercase">Ask notebook question:</span>
                    <div className="flex gap-2">
                      <input 
                        type="text" 
                        value={notebookQuery}
                        onChange={(e) => setNotebookQuery(e.target.value)}
                        placeholder="Can we route payments through SecurePayment?"
                        className="flex-1 bg-black border border-zinc-800 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-amber-400/40"
                      />
                      <button 
                        onClick={() => {
                          if (notebookQuery.trim() === '') return;
                          if (!handleActionSpend()) return;
                          // If search query mentions "SecurePayment" or "payment" - it is grounded, else warn
                          const q = notebookQuery.toLowerCase();
                          if (q.includes('SecurePayment') || q.includes('coffee') || q.includes('order')) {
                            setQueryGrounded(true);
                            setNotebooks(prev => prev.map(n => {
                              if (n.id === activeNotebookId) {
                                return {
                                  ...n,
                                  aiResponse: `Response (Grounded in Sources): Found associated active partner registers. Account Name: Secure Billing Provider (Sort Code: 04-06-05, Account Number: 31396515). Verification key aligned under NexoraX Digital Ltd.`
                                };
                              }
                              return n;
                            }));
                          } else {
                            setQueryGrounded(false);
                            setNotebooks(prev => prev.map(n => {
                              if (n.id === activeNotebookId) {
                                return {
                                  ...n,
                                  aiResponse: `Response (Warning: Not Grounded): This question falls outside coffee shop conceptual parameters. I am summarizing using generic knowledge models instead.`
                                };
                              }
                              return n;
                            }));
                          }
                          setNotebookQuery('');
                        }}
                        className="p-2.5 bg-amber-500 text-black rounded-xl hover:scale-[1.02] cursor-pointer transition-all flex items-center justify-center shrink-0"
                      >
                        <Send className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

            </motion.div>
          )}

          {/* ==================== SCREEN 3: AI PROMPT STUDIO ==================== */}
          {activeScreen === 'prompt' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="grid grid-cols-1 lg:grid-cols-12 gap-6 text-left">
              
              {/* Left Settings & Templates panel */}
              <div className="lg:col-span-4 space-y-4 bg-zinc-950/80 border border-zinc-800 rounded-3xl p-6">
                <h3 className="text-xs font-mono font-black uppercase text-cyan-400 tracking-wider">Playground Options</h3>

                <div className="space-y-3">
                  <div>
                    <label className="text-[10px] font-mono text-slate-400 uppercase block mb-1">Model Engine Provider</label>
                    <select 
                      value={selectedModel} 
                      onChange={(e) => setSelectedModel(e.target.value)}
                      className="w-full bg-black border border-zinc-800 rounded-xl px-3 py-2 text-xs text-slate-300 focus:outline-none"
                    >
                      <option value="gemini-2.5-pro">Gemini 2.5 Pro - Global standard</option>
                      <option value="gemini-2.5-flash">Gemini 2.5 Flash - High speed</option>
                      <option value="nexus-architect">NexoraX Architect Neural-9</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-[10px] font-mono text-slate-400 uppercase block mb-1">Format Destination Type</label>
                    <select 
                      value={selectedFormat} 
                      onChange={(e) => setSelectedFormat(e.target.value)}
                      className="w-full bg-black border border-zinc-800 rounded-xl px-3 py-2 text-xs text-slate-300 focus:outline-none"
                    >
                      <option value="Landing page copy">Landing page copy</option>
                      <option value="Sales Email Draft">Sales Email Draft</option>
                      <option value="SEO target guidelines">SEO target guidelines</option>
                      <option value="JSON state payload">JSON state payload</option>
                    </select>
                  </div>

                  <div>
                    <div className="flex justify-between text-[10px] font-mono text-slate-400 mb-1">
                      <span>Temperature</span>
                      <span>{promptTemperature}</span>
                    </div>
                    <input 
                      type="range" 
                      min="0.1" 
                      max="1.5" 
                      step="0.1" 
                      value={promptTemperature}
                      onChange={(e) => setPromptTemperature(parseFloat(e.target.value))}
                      className="w-full accent-[#00ffff]"
                    />
                  </div>

                  <div className="pt-2 border-t border-zinc-900 space-y-2">
                    <span className="text-[10px] font-mono text-slate-500 uppercase block">Sample Workspace Presets</span>
                    <div className="grid grid-cols-2 gap-1.5">
                      {[
                        { name: 'Business Idea', prompt: 'Structure a custom burger delivery micro web service outline' },
                        { name: 'Sales Email', prompt: 'Write an email offering direct-billing NexoraX billing records placements' },
                        { name: 'Support FAQ', prompt: 'Draft clean FAQ questions covering zero chargeback policies' },
                        { name: 'SEO target', prompt: 'Top 5 focus keywords to optimize restaurant local SEO listings' }
                      ].map((preset, idx) => (
                        <button
                          key={idx}
                          onClick={() => {
                            setUserPrompt(preset.prompt);
                            onAddLog('AI Prompt Studio', `Loaded preset trigger: "${preset.name}"`, 'SUCCESS', 'Form presets filled');
                          }}
                          className="bg-black/80 hover:bg-zinc-900 border border-zinc-800/80 p-2 text-[10px] rounded-lg text-slate-300 text-left cursor-pointer transition-all"
                        >
                          {preset.name}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Center Work area */}
              <div className="lg:col-span-8 space-y-4 bg-zinc-950/80 border border-zinc-800 rounded-3xl p-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-mono font-black uppercase text-[#e840f3] tracking-wider">Prompt Canvas Editor</h3>
                  <button 
                    onClick={() => {
                      if (!handleActionSpend()) return;
                      // Simulated playground run
                      let generatedText = `[PROMPT PREVIEW OUTLINE]\nFormat: ${selectedFormat}\nModel: ${selectedModel}\n\n`;
                      if (userPrompt.toLowerCase().includes('email')) {
                        generatedText += `Subject: Operational Proposal\n\nDear recipient,\nFollowing critical analysis of modern web conversion metrics, we suggest deploying a zero friction landing container. Please review support structures at support@nexoraxdigital.co.uk.\n\nRespectfully,\nSystems Lead`;
                      } else {
                        generatedText += `Title: Premium Infrastructure Solutions\n\n- Quick checkout containers decrease billing wait time by 45%.\n- Zero arbitrary logs prevents leakage.\n- Active synchronization ensures real-time metrics are correct.`;
                      }
                      setPromptOutput(generatedText);
                      setPromptHistory(prev => [{ prompt: userPrompt, format: selectedFormat, output: generatedText }, ...prev]);
                      onAddLog('AI Prompt Studio', 'Executed prompt playground trial', 'SUCCESS', 'Output compiled successfully');
                    }}
                    className="px-4 py-2 bg-gradient-to-r from-[#e840f3] to-indigo-600 hover:scale-[1.02] text-white text-[11px] font-mono font-black uppercase rounded-xl tracking-wider cursor-pointer"
                  >
                    Run Playground
                  </button>
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="text-[10px] font-mono text-slate-500 uppercase mb-1 block">System Instructions</label>
                    <input 
                      type="text"
                      value={systemInstruction}
                      onChange={(e) => setSystemInstruction(e.target.value)}
                      className="w-full bg-black border border-zinc-900 rounded-xl px-3 py-2 text-xs text-white focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="text-[10px] font-mono text-slate-500 uppercase mb-1 block">User Prompt Input</label>
                    <textarea 
                      value={userPrompt}
                      onChange={(e) => setUserPrompt(e.target.value)}
                      className="w-full h-24 bg-black border border-zinc-900 rounded-xl p-3 text-xs text-white focus:outline-none focus:border-[#e840f3]/40 leading-relaxed"
                    />
                  </div>

                  <div>
                    <label className="text-[10px] font-mono text-slate-500 uppercase mb-1 block">Compiled Prompt Output (Grounded & Safe)</label>
                    <div className="w-full min-h-[140px] bg-black/60 border border-zinc-900 rounded-2xl p-4 text-xs font-mono text-slate-300 leading-relaxed whitespace-pre-wrap">
                      {promptOutput || 'Click Run Playground to stream the compiled output response...'}
                    </div>
                  </div>
                </div>
              </div>

            </motion.div>
          )}

          {/* ==================== SCREEN 4: AGENT MISSION CONTROL ==================== */}
          {activeScreen === 'agent' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6 text-left">
              <div className="bg-zinc-950/80 border border-zinc-800 rounded-3xl p-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-zinc-900 pb-4 mb-4">
                  <div>
                    <h3 className="text-sm font-mono font-black uppercase text-amber-500 tracking-wider">Antigravity-Style Mission Dispatcher</h3>
                    <p className="text-xs text-slate-400">Initialize tasks for autonomous background engines. Verifiable results are rendered on completion.</p>
                  </div>
                  
                  {/* Dispatcher Actions */}
                  <div className="flex gap-2">
                    <input 
                      type="text" 
                      value={targetMissionTitle}
                      onChange={(e) => setTargetMissionTitle(e.target.value)}
                      placeholder="e.g., Competitor analysis for takeaway"
                      className="bg-black border border-zinc-800 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-amber-400"
                    />
                    <button 
                      onClick={() => {
                        if (targetMissionTitle.trim() === '') return;
                        if (!handleActionSpend()) return;
                        const newMission = {
                          id: 'm-' + Date.now(),
                          title: targetMissionTitle,
                          status: 'Planning',
                          progress: 10,
                          artifacts: {
                            plan: 'Preparing research framework and routing metrics.',
                            report: 'Pending execution logs...'
                          }
                        };
                        setMissions([newMission, ...missions]);
                        setTargetMissionTitle('');
                        onAddLog('Agent Mission Control', `Dispatched mission: "${newMission.title}"`, 'SUCCESS', 'Agent registered to Vanguard Node');
                      }}
                      className="px-4 py-2 bg-amber-500 text-black text-[11px] font-mono font-black uppercase rounded-xl hover:scale-[1.02] cursor-pointer"
                    >
                      Dispatch Mission
                    </button>
                  </div>
                </div>

                {/* Missions Active Rows */}
                <div className="space-y-4">
                  {missions.map((mission, idx) => (
                    <div key={idx} className="p-5 rounded-2xl bg-black border border-zinc-900/80 space-y-4 relative overflow-hidden group">
                      
                      {/* Top Header tracking progress and state */}
                      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                        <div className="space-y-1">
                          <h4 className="text-sm font-bold text-slate-100">{mission.title}</h4>
                          <span className="text-[10px] text-slate-500 font-mono">Mission ID: {mission.id}</span>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] font-mono font-black px-2 py-0.5 rounded uppercase ${
                            mission.status === 'Completed' 
                            ? 'bg-emerald-950 text-emerald-400 border border-emerald-500/30' 
                            : 'bg-amber-950 text-amber-400 border border-amber-500/30'
                          }`}>
                            {mission.status}
                          </span>
                          <span className="text-xs text-slate-400 font-mono">Progress: {mission.progress}%</span>
                        </div>
                      </div>

                      {/* Display Progress line animation elements */}
                      <div className="w-full bg-zinc-900 rounded-full h-1.5 overflow-hidden">
                        <div 
                          className={`h-full transition-all duration-1000 ${mission.status === 'Completed' ? 'bg-emerald-500' : 'bg-amber-500'}`}
                          style={{ width: `${mission.progress}%` }}
                        ></div>
                      </div>

                      {/* Code and log outputs draft previews */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 text-xs">
                        <div className="p-3 bg-zinc-950 rounded-xl border border-zinc-900">
                          <span className="text-[9px] font-mono uppercase text-slate-500 block mb-1">Predefined Task Framework</span>
                          <p className="text-xs text-slate-300 font-sans">{mission.artifacts.plan}</p>
                        </div>
                        <div className="p-3 bg-zinc-950 rounded-xl border border-zinc-900">
                          <span className="text-[9px] font-mono uppercase text-slate-500 block mb-1">Draft Artifact Output Report</span>
                          <p className="text-xs text-slate-300 font-sans">{mission.artifacts.report}</p>
                        </div>
                      </div>

                      {/* Approval security buttons checking safety compliance */}
                      <div className="flex items-center justify-between border-t border-zinc-900/60 pt-3 text-[11px] font-mono">
                        <div className="flex items-center gap-1.5 text-slate-500">
                          <Info className="w-3.5 h-3.5" />
                          <span>Human approval is strictly mandatory before email transmission or external billing</span>
                        </div>

                        {mission.status !== 'Completed' && (
                          <div className="flex gap-2">
                            <button 
                              onClick={() => {
                                setMissions(prev => prev.map(m => {
                                  if (m.id === mission.id) {
                                    return {
                                      ...m,
                                      status: 'Completed',
                                      progress: 100,
                                      artifacts: {
                                        plan: m.artifacts.plan,
                                        report: 'Human verified plan: Deploy direct checkout container immediately.'
                                      }
                                    };
                                  }
                                  return m;
                                }));
                                onAddLog('Agent Mission Control', `Human Approved Mission ID: ${mission.id}`, 'SUCCESS', 'Proposal files marked active');
                              }}
                              className="px-3 py-1.5 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-lg hover:bg-emerald-500/20 font-bold"
                            >
                              Approve Artifact
                            </button>
                            <button 
                              onClick={() => {
                                setMissions(prev => prev.map(m => {
                                  if (m.id === mission.id) {
                                    return {
                                      ...m,
                                      status: 'Failed safely',
                                      progress: 0,
                                      artifacts: {
                                        plan: 'Mission aborted by manual trigger.',
                                        report: 'Task closed securely without leaking environment secrets.'
                                      }
                                    };
                                  }
                                  return m;
                                }));
                                onAddLog('Agent Mission Control', `Mission ID ${mission.id} aborted`, 'WARNING', 'Execution terminated safely');
                              }}
                              className="px-3 py-1.5 bg-rose-950/20 text-rose-400 border border-rose-500/20 rounded-lg hover:bg-rose-950/40"
                            >
                              Abort
                            </button>
                          </div>
                        )}
                      </div>

                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {/* ==================== SCREEN 5: APP BUILDER CANVAS ==================== */}
          {activeScreen === 'app' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="grid grid-cols-1 lg:grid-cols-12 gap-6 text-left">
              
              {/* Left Config Panel */}
              <div className="lg:col-span-4 bg-zinc-950/80 border border-zinc-800 rounded-3xl p-6 space-y-4">
                <h3 className="text-xs font-mono font-black uppercase text-cyan-400 tracking-wider">Canvas Customizer</h3>

                <div className="space-y-3">
                  <div>
                    <label className="text-[10px] font-mono text-slate-400 uppercase block mb-1">What would you like to build?</label>
                    <textarea 
                      value={appIdea} 
                      onChange={(e) => setAppIdea(e.target.value)} 
                      placeholder="e.g., pricing calculator for fitness coaches"
                      className="w-full h-24 bg-black border border-zinc-850 rounded-xl p-3 text-xs text-white focus:outline-none focus:border-cyan-400/30"
                    />
                  </div>

                  <div>
                    <label className="text-[10px] font-mono text-slate-400 uppercase block mb-1">App Blueprint Presets</label>
                    <select 
                      value={selectedAppTemplate} 
                      onChange={(e) => setSelectedAppTemplate(e.target.value)}
                      className="w-full bg-black border border-zinc-800 rounded-xl px-3 py-2 text-xs text-slate-300 focus:outline-none"
                    >
                      <option value="Landing Page">Landing Page Draft</option>
                      <option value="Pricing Calculator">Pricing Calculator</option>
                      <option value="Booking Form">Contact & Booking form</option>
                      <option value="Charity Donation Node">Charity Campaign Details</option>
                    </select>
                  </div>

                  <button 
                    onClick={handleGenerateApp}
                    className="w-full py-3 bg-gradient-to-r from-cyan-500 to-teal-500 hover:scale-[1.02] text-black font-mono text-xs font-black uppercase rounded-xl tracking-wider transition-all cursor-pointer flex items-center justify-center gap-2 shadow-[0_0_15px_rgba(6,182,212,0.25)]"
                  >
                    <Play className="w-4 h-4 fill-black" />
                    Generate Structure
                  </button>
                </div>
              </div>

              {/* Right Results Output */}
              <div className="lg:col-span-8 bg-zinc-950/80 border border-zinc-800 rounded-3xl p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-mono font-black uppercase text-purple-400 tracking-wider">Generated Structure Preview</h3>
                  {generatedAppStructure && (
                    <button 
                      onClick={() => {
                        // Open publishing hub
                        setActiveScreen('publish');
                      }}
                      className="px-3 py-1.5 bg-[#e840f3]/10 text-[#e840f3] border border-[#e840f3]/25 rounded-lg text-[10px] font-mono uppercase"
                    >
                      Ready to Publish
                    </button>
                  )}
                </div>

                {generatedAppStructure ? (
                  <div className="space-y-4 text-xs font-mono">
                    <div className="p-4 bg-black rounded-2xl border border-zinc-850 space-y-2">
                      <div className="flex justify-between border-b border-zinc-900 pb-2">
                        <span className="text-[10px] text-slate-500 font-mono uppercase">App Project Name:</span>
                        <span className="text-cyan-400 text-xs font-bold">{generatedAppStructure.name}</span>
                      </div>
                      <div className="flex justify-between border-b border-zinc-900 pb-2 pt-1">
                        <span className="text-[10px] text-slate-500 font-mono uppercase">Target Template:</span>
                        <span className="text-slate-300">{generatedAppStructure.template}</span>
                      </div>
                      <div className="flex justify-between pt-1">
                        <span className="text-[10px] text-slate-500 font-mono uppercase">Hosting Recommendation:</span>
                        <span className="text-amber-400 text-xs">{generatedAppStructure.hostingRecommendation}</span>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <span className="text-[10px] text-slate-400 uppercase tracking-wider block">Component Tree Layout:</span>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {generatedAppStructure.structure.map((item: any, idx: number) => (
                          <div key={idx} className="p-3 bg-black/60 border border-zinc-900 rounded-xl">
                            <span className="text-[11px] text-[#e840f3] block">{item.name}</span>
                            <p className="font-sans text-[11px] text-slate-400 mt-0.5 leading-normal">{item.details}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="p-3 bg-[#00ffff]/5 border border-[#00ffff]/20 rounded-xl font-sans space-y-1">
                      <span className="text-[10px] font-mono uppercase text-[#00ffff]">Meta Headers Content:</span>
                      <p className="text-[11px] text-slate-300"><strong>Title:</strong> {generatedAppStructure.seoMetadata.title}</p>
                      <p className="text-[11px] text-slate-400"><strong>Description:</strong> {generatedAppStructure.seoMetadata.description}</p>
                    </div>

                    <div className="flex gap-2">
                      <button 
                        onClick={() => {
                          const code = `// Compiled structure for ${generatedAppStructure.name} Ready to load`;
                          setFilesData(prev => ({
                            ...prev,
                            'app.js': code,
                            'index.html': `<!-- Custom dynamic page for ${generatedAppStructure.name} -->\n${filesData['index.html']}`
                          }));
                          onAddLog('App Builder Canvas', 'Pushed app source snippets to code workspace', 'SUCCESS', 'Code tabs synced');
                          setActiveScreen('code');
                        }}
                        className="px-4 py-2 bg-zinc-900 border border-zinc-700 text-xs font-bold font-mono text-slate-200 rounded-lg hover:bg-zinc-850"
                      >
                        Push Code Snippets To Code Tab
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center p-12 bg-black/40 border border-dashed border-zinc-800 rounded-2xl text-center space-y-4">
                    <Layout className="w-12 h-12 text-zinc-600 animate-pulse" />
                    <div>
                      <h4 className="text-sm font-mono text-zinc-400 uppercase">Generate Draft Project Blueprint</h4>
                      <p className="text-xs text-zinc-500 max-w-sm mt-1">Describe your business utility concept inside the customizer and build a real visual component layout schema mapped to the platform hosting service.</p>
                    </div>
                  </div>
                )}
              </div>

            </motion.div>
          )}

          {/* ==================== SCREEN 6: AUTOMATION FLOW BUILDER ==================== */}
          {activeScreen === 'flow' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6 text-left">
              <div className="bg-zinc-950/80 border border-zinc-800 rounded-3xl p-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-zinc-900 pb-4 mb-6">
                  <div>
                    <h3 className="text-sm font-mono font-black uppercase text-pink-500 tracking-wider">No-Code Trigger Action Automations</h3>
                    <p className="text-xs text-slate-400">Wire event hooks dynamically to execute core system operations immediately.</p>
                  </div>

                  {/* Flow Creator Panel */}
                  <div className="flex flex-wrap gap-2">
                    <select 
                      value={newFlowTrigger} 
                      onChange={(e) => setNewFlowTrigger(e.target.value)}
                      className="bg-black border border-zinc-850 rounded-xl px-3 py-2 text-xs text-white"
                    >
                      <option value="New search saved">New search saved</option>
                      <option value="New chat lead">New chat lead</option>
                      <option value="New form submitted">New form submitted</option>
                      <option value="New email received">New email received</option>
                    </select>

                    <span className="text-slate-500 text-xs pt-2">→</span>

                    <select 
                      value={newFlowAction} 
                      onChange={(e) => setNewFlowAction(e.target.value)}
                      className="bg-black border border-zinc-850 rounded-xl px-3 py-2 text-xs text-white"
                    >
                      <option value="Create WorkSuite task">Create WorkSuite task</option>
                      <option value="Create notebook note">Create notebook note</option>
                      <option value="Request approval">Request approval</option>
                    </select>

                    <button 
                      onClick={() => {
                        const newF = {
                          id: 'flow-' + Date.now(),
                          name: `${newFlowTrigger.split(' ')[1]} Hook to ${newFlowAction.split(' ')[1]}`,
                          trigger: newFlowTrigger,
                          action: newFlowAction,
                          active: true
                        };
                        setFlows([...flows, newF]);
                        onAddLog('Automation Flow Builder', `Established pipeline hook: "${newF.name}"`, 'SUCCESS', 'Trigger active');
                      }}
                      className="px-4 py-2 bg-pink-500 text-black text-[11px] font-mono font-black uppercase rounded-xl hover:scale-[1.02] cursor-pointer"
                    >
                      Construct Hook
                    </button>
                  </div>
                </div>

                {/* Flow Visual Diagrams */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {flows.map((flow) => (
                    <div key={flow.id} className="p-5 rounded-2xl bg-black border border-zinc-900/80 space-y-4 relative">
                      <div className="flex justify-between items-center">
                        <strong className="text-xs font-mono font-black uppercase tracking-wide text-slate-200">{flow.name}</strong>
                        
                        <div className="flex items-center gap-1.5">
                          <span className="text-[10px] text-slate-500">Active Node</span>
                          <input 
                            type="checkbox" 
                            checked={flow.active}
                            onChange={(e) => {
                              const active = e.target.checked;
                              setFlows(prev => prev.map(f => f.id === flow.id ? { ...f, active } : f));
                              onAddLog('Automation Flow Builder', `Toggled flow state for ${flow.name}`, 'SUCCESS', `Status: ${active ? 'Online' : 'Offline'}`);
                            }}
                            className="accent-pink-500 rounded cursor-pointer"
                          />
                        </div>
                      </div>

                      {/* Visual Flow diagram blocks */}
                      <div className="flex items-center justify-between p-3 bg-zinc-950 rounded-xl border border-zinc-900 text-xs font-mono">
                        <div className="p-2 bg-purple-950/40 text-purple-400 border border-purple-500/20 rounded-lg text-center shrink-0 min-w-[100px]">
                          <span className="block text-[8px] text-slate-500">TRIGGER</span>
                          <strong>{flow.trigger}</strong>
                        </div>
                        <div className="flex-1 border-t border-dashed border-zinc-800 mx-3 flex justify-center">
                          <Workflow className="w-4 h-4 text-slate-600" />
                        </div>
                        <div className="p-2 bg-cyan-950/40 text-[#00ffff] border border-cyan-500/20 rounded-lg text-center shrink-0 min-w-[100px]">
                          <span className="block text-[8px] text-slate-500">ACTION</span>
                          <strong>{flow.action}</strong>
                        </div>
                      </div>

                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {/* ==================== SCREEN 7: RESEARCH & SEARCH LAB ==================== */}
          {activeScreen === 'search' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="grid grid-cols-1 lg:grid-cols-12 gap-6 text-left">
              
              {/* Left Panel: Run query */}
              <div className="lg:col-span-8 bg-zinc-950/80 border border-zinc-800 rounded-3xl p-6 space-y-4">
                <div className="flex items-center justify-between border-b border-zinc-900 pb-3">
                  <h3 className="text-xs font-mono font-black uppercase text-cyan-400 tracking-wider">NexoraX Web Search intelligence</h3>
                  <span className="text-[10px] text-slate-400 font-mono">Charges 1 Search Credit per run</span>
                </div>

                <div className="flex gap-2">
                  <select 
                    value={searchType} 
                    onChange={(e) => setSearchType(e.target.value)}
                    className="bg-black border border-zinc-850 rounded-xl px-3 py-2 text-xs text-white"
                  >
                    <option value="Web search">Web search</option>
                    <option value="Competitor search">Competitor search</option>
                    <option value="Product search">Product search</option>
                  </select>

                  <input 
                    type="text" 
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search keywords, business trends, price data..."
                    className="flex-1 bg-black border border-zinc-850 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-cyan-400/35"
                    onKeyDown={(e) => { if (e.key === 'Enter') handleRunSearch(); }}
                  />

                  <button 
                    onClick={handleRunSearch}
                    className="px-5 py-2 bg-gradient-to-r from-cyan-500 to-indigo-500 hover:scale-[1.02] text-black font-mono text-xs font-black uppercase rounded-xl cursor-pointer"
                  >
                    Search
                  </button>
                </div>

                <div className="space-y-4">
                  {searchResults.length > 0 ? (
                    searchResults.map((res) => (
                      <div key={res.id} className="p-4 bg-black/60 border border-zinc-900 rounded-xl space-y-2 hover:border-zinc-800 transition-all">
                        <div className="flex justify-between items-start">
                          <h4 className="text-xs font-mono font-black uppercase text-[#00ffff] hover:underline cursor-pointer">{res.title}</h4>
                          <span className="text-[9px] font-mono uppercase bg-zinc-900 text-slate-500 px-1.5 py-0.5 rounded border border-zinc-800/40">
                            {searchType}
                          </span>
                        </div>
                        <p className="text-xs text-slate-300 leading-relaxed font-sans">{res.snippet}</p>
                        
                        <div className="flex gap-2 pt-2 border-t border-zinc-950 text-[10px] font-mono">
                          <button 
                            onClick={() => {
                              setNotebooks(prev => prev.map(n => {
                                if (n.id === activeNotebookId) {
                                  return {
                                    ...n,
                                    sources: [...n.sources, { type: 'search', text: `${res.title}: ${res.snippet}` }]
                                  };
                                }
                                return n;
                              }));
                              onAddLog('Research Lab', 'Saved active search data to notebook sources', 'SUCCESS', 'Database synced');
                              setActiveScreen('notebook');
                            }}
                            className="text-purple-400 hover:text-purple-300 uppercase tracking-widest"
                          >
                            Save to active Notebook
                          </button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="flex flex-col items-center justify-center py-12 text-center text-zinc-500 space-y-2">
                      <Search className="w-8 h-8 text-zinc-600 animate-pulse" />
                      <p className="text-xs font-mono text-slate-500">Query results will render inside this container.</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Right Panel: Purchase Credits packs */}
              <div className="lg:col-span-4 bg-zinc-950/80 border border-zinc-800 rounded-3xl p-6 space-y-4">
                <h3 className="text-xs font-mono font-black uppercase text-amber-500 tracking-wider">Purchase Search credit bundles</h3>

                <div className="space-y-3">
                  {[
                    { name: 'Explorer Pack', price: 5, amount: 500 },
                    { name: 'Growth Pack', price: 10, amount: 1000 },
                    { name: 'Nebula Pack', price: 25, amount: 2750 },
                    { name: 'Cosmic Pack', price: 100, amount: 13000 }
                  ].map((pack, idx) => (
                    <div key={idx} className="p-3 bg-black border border-zinc-850 rounded-xl flex items-center justify-between hover:border-amber-500/20 transition-all text-xs">
                      <div>
                        <strong className="block font-bold text-white font-mono uppercase tracking-wide text-[11px]">{pack.name}</strong>
                        <span className="text-slate-400 font-mono text-[10px]">{pack.amount} search credits</span>
                      </div>
                      <button 
                        onClick={() => {
                          setSearchCredits(prev => prev + pack.amount);
                          onAddSearchCredits(pack.price, pack.amount);
                          onAddLog('Billing', `Purchased search credit pack: "${pack.name}"`, 'SUCCESS', `Added ${pack.amount} credits. Funds routed to secure checkout.`);
                        }}
                        className="px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-black font-mono font-black tracking-wider uppercase text-[10px] rounded-lg"
                      >
                        £{pack.price}
                      </button>
                    </div>
                  ))}
                </div>
              </div>

            </motion.div>
          )}

          {/* ==================== SCREEN 8: CODE & FILE WORKSPACE ==================== */}
          {activeScreen === 'code' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="grid grid-cols-1 lg:grid-cols-12 gap-6 text-left">
              
              {/* File Tree panel */}
              <div className="lg:col-span-3 bg-zinc-950/80 border border-zinc-800 rounded-3xl p-6 space-y-3">
                <h3 className="text-xs font-mono font-black uppercase text-cyan-400 tracking-wider">Code Explorer</h3>

                <div className="space-y-1">
                  {Object.keys(filesData).map((fName) => (
                    <div 
                      key={fName}
                      onClick={() => setSelectedFile(fName)}
                      className={`p-2.5 rounded-xl text-xs font-mono flex items-center justify-between cursor-pointer transition-all ${
                        selectedFile === fName 
                        ? 'bg-purple-950/40 text-purple-300 border border-purple-500/30' 
                        : 'text-slate-400 hover:bg-zinc-900/60'
                      }`}
                    >
                      <span className="flex items-center gap-1.5">
                        <FileCode className="w-3.5 h-3.5" /> {fName}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Editor panel */}
              <div className="lg:col-span-9 bg-zinc-950/80 border border-zinc-800 rounded-3xl p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-mono font-black uppercase text-amber-500 tracking-wider">Live Text Snippet - {selectedFile}</h3>
                  <button 
                    onClick={() => {
                      onAddLog('Code Workspace', `Saved edited snippet lines: ${selectedFile}`, 'SUCCESS', 'Updated index components');
                    }}
                    className="px-3 py-1.5 bg-amber-500 text-black text-[10px] font-mono font-bold uppercase rounded-lg hover:scale-102 cursor-pointer"
                  >
                    Save Snippet
                  </button>
                </div>

                <textarea 
                  value={filesData[selectedFile]}
                  onChange={(e) => {
                    const newVal = e.target.value;
                    setFilesData((prev: any) => ({
                      ...prev,
                      [selectedFile]: newVal
                    }));
                  }}
                  className="w-full h-96 bg-black border border-zinc-900 rounded-2xl p-4 text-xs font-mono text-[#00ffc4] leading-relaxed focus:outline-none"
                />

                <div className="p-3 bg-zinc-900/60 rounded-xl text-[10px] font-mono text-slate-500 flex items-center gap-2">
                  <Lock className="w-3 h-3 text-amber-500" />
                  <span>Interactive file container is sandboxed. Free execution has zero shell access to shield private keys and infrastructure.</span>
                </div>
              </div>

            </motion.div>
          )}

          {/* ==================== SCREEN 9: MEDIA CREATION DOCK ==================== */}
          {activeScreen === 'media' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="grid grid-cols-1 lg:grid-cols-12 gap-6 text-left">
              
              {/* Creator Panel */}
              <div className="lg:col-span-5 bg-zinc-950/80 border border-zinc-800 rounded-3xl p-6 space-y-4">
                <h3 className="text-xs font-mono font-black uppercase text-[#e840f3] tracking-wider">Campaign Prompt</h3>

                <div className="space-y-3">
                  <div>
                    <label className="text-[10px] font-mono text-slate-400 block mb-1 uppercase">Media Description Prompt</label>
                    <textarea 
                      value={mediaPrompt}
                      onChange={(e) => setMediaPrompt(e.target.value)}
                      className="w-full h-24 bg-black border border-zinc-850 rounded-xl p-3 text-xs text-white focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="text-[10px] font-mono text-slate-400 block mb-1 uppercase">Visual Style</label>
                    <select 
                      value={mediaStyle} 
                      onChange={(e) => setMediaStyle(e.target.value)}
                      className="w-full bg-black border border-zinc-805 rounded-xl px-3 py-2 text-xs text-slate-300 focus:outline-none"
                    >
                      <option value="Futuristic Tech">Futuristic Tech Space</option>
                      <option value="Modern Swiss Editorial">Modern Swiss Editorial</option>
                      <option value="UGC Video Portrait">UGC Promo Video Portrait</option>
                    </select>
                  </div>

                  <button 
                    onClick={() => {
                      if (!handleActionSpend()) return;
                      const outputPreview = `[SIMULATED MEDIA GENERATED]\nStyle: ${mediaStyle}\nPrompt: ${mediaPrompt}\nStatus: Render Complete (Watermarked)`;
                      setMediaOutputs([{ type: 'Image Asset', preview: outputPreview, prompt: mediaPrompt, status: 'Draft Preview' }, ...mediaOutputs]);
                      onAddLog('Media Creation Dock', 'Rendered watermarked promo media asset', 'SUCCESS', 'Loaded media generator models');
                    }}
                    className="w-full py-3 bg-[#e840f3] hover:scale-[1.02] text-white text-xs font-mono font-black uppercase tracking-wider rounded-xl transition-all"
                  >
                    Stream Media Preview
                  </button>
                </div>
              </div>

              {/* Outputs Dock */}
              <div className="lg:col-span-7 bg-zinc-950/80 border border-zinc-800 rounded-3xl p-6 space-y-4">
                <h3 className="text-xs font-mono font-black uppercase text-cyan-400 tracking-wider">Preview Assets Box</h3>

                {mediaOutputs.length > 0 ? (
                  <div className="space-y-4">
                    {mediaOutputs.map((out, idx) => (
                      <div key={idx} className="p-4 bg-black border border-zinc-900 rounded-xl text-xs font-mono space-y-2">
                        <div className="flex justify-between items-center text-[10px]">
                          <strong>{out.type}</strong>
                          <span className="bg-amber-950 text-amber-400 border border-amber-500/30 px-1.5 py-0.5 rounded uppercase">
                            {out.status}
                          </span>
                        </div>
                        
                        <div className="p-3 bg-zinc-950 rounded-lg text-slate-300 whitespace-pre-wrap border border-zinc-900">
                          {out.preview}
                        </div>

                        <div className="flex justify-between items-center text-[10px] pt-1">
                          <span className="text-slate-500">Watermark: NEXORAX GALAXY LAB FREE</span>
                          <button 
                            onClick={() => {
                              onAddLog('Billing', 'User checked upgrade options for watermarked media', 'SUCCESS', 'Render checkout initialized');
                              setShowLimitWarning(true);
                            }}
                            className="text-amber-400 font-bold uppercase"
                          >
                            Remove Watermark
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center p-12 text-center text-zinc-500 space-y-3">
                    <Video className="w-10 h-10 text-zinc-700 animate-pulse" />
                    <div>
                      <p className="text-xs font-mono text-zinc-400 uppercase">Interactive Previews box</p>
                      <p className="text-[11px] text-zinc-500 mt-1 max-w-xs mx-auto">Generate thumbnail layout graphics, voice-over transcripts, or promotional UGC storyboards directly aligned to your active campaign plans.</p>
                    </div>
                  </div>
                )}
              </div>

            </motion.div>
          )}

          {/* ==================== SCREEN 10: WORKSUITE PROJECT BOARD ==================== */}
          {activeScreen === 'worksuite' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6 text-left">
              <div className="bg-zinc-950/80 border border-zinc-800 rounded-3xl p-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between border-b border-zinc-900 pb-4 mb-4 gap-4">
                  <div>
                    <h3 className="text-sm font-mono font-black uppercase text-cyan-400 tracking-wider">Galaxy Lab WorkSuite Hub</h3>
                    <p className="text-xs text-slate-400 font-sans">Organize active draft tasks, agent missions, and client approval lists.</p>
                  </div>

                  <div className="flex gap-2">
                    <input 
                      type="text" 
                      value={newSuiteTaskTitle}
                      onChange={(e) => setNewSuiteTaskTitle(e.target.value)}
                      placeholder="Enter workflow action card..."
                      className="bg-black border border-zinc-800 rounded-xl px-3 py-2 text-xs text-white focus:outline-none"
                    />
                    <button 
                      onClick={() => {
                        if (!newSuiteTaskTitle.trim()) return;
                        const t: WorkSuiteTask = {
                          id: 't-int-' + Date.now(),
                          title: newSuiteTaskTitle,
                          project: 'Galaxy Lab',
                          client: user.displayName,
                          priority: 'LOW',
                          status: 'TODO'
                        };
                        setWorksuiteTasks([t, ...worksuiteTasks]);
                        onAddTaskToWorkSuite(t);
                        setNewSuiteTaskTitle('');
                        onAddLog('WorkSuite PM', `Injected task checklist item: "${t.title}"`, 'SUCCESS', 'Pushed directly to master WorkSuite layout');
                      }}
                      className="px-4 py-2 bg-[#00ffff] text-black text-[11px] font-mono font-black uppercase rounded-xl"
                    >
                      Inscribe
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {['TODO', 'IN_PROGRESS', 'COMPLETED'].map((col) => (
                    <div key={col} className="bg-black/40 border border-zinc-900 rounded-2xl p-4 space-y-3">
                      <span className="text-[10px] font-mono font-black uppercase text-slate-500 tracking-widest block">
                        {col === 'TODO' ? '📋 TO DO LIST' : col === 'IN_PROGRESS' ? '⚡ IN RUN STATUS' : '✅ COMPLETED'}
                      </span>
                      
                      <div className="space-y-2 h-80 overflow-y-auto">
                        {worksuiteTasks.filter(item => item.status === col).map((task) => (
                          <div key={task.id} className="p-3 bg-black border border-zinc-850 hover:border-zinc-700 rounded-xl text-xs relative space-y-2">
                            <h4 className="font-sans font-bold text-slate-200">{task.title}</h4>
                            
                            <div className="flex justify-between items-center text-[9px] font-mono text-slate-500">
                              <span>Priority: <strong className="text-amber-500">{task.priority}</strong></span>
                              <span>{task.client}</span>
                            </div>

                            {col !== 'COMPLETED' && (
                              <button 
                                onClick={() => {
                                  const nextSt: any = col === 'TODO' ? 'IN_PROGRESS' : 'COMPLETED';
                                  setWorksuiteTasks(prev => prev.map(pt => pt.id === task.id ? { ...pt, status: nextSt } : pt));
                                  onAddLog('WorkSuite PM', `Completed task progression card: "${task.title}"`, 'SUCCESS', 'Column sync completed');
                                }}
                                className="w-full py-1 bg-zinc-900 hover:bg-zinc-800 rounded text-[9px] font-mono uppercase tracking-widest text-slate-400"
                              >
                                Advance Stage
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {/* ==================== SCREEN 11: PUBLISH & HOSTING HUB ==================== */}
          {activeScreen === 'publish' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="grid grid-cols-1 lg:grid-cols-12 gap-6 text-left">
              
              {/* Left Domain Setup Checker */}
              <div className="lg:col-span-5 bg-zinc-950/80 border border-zinc-800 rounded-3xl p-6 space-y-4">
                <h3 className="text-xs font-mono font-black uppercase text-cyan-400 tracking-wider">Publishing Draft Config</h3>

                <div className="space-y-4">
                  <div>
                    <label className="text-[10px] font-mono text-slate-400 block mb-1 uppercase">Custom Domain server address</label>
                    <div className="flex gap-2">
                      <input 
                        type="text" 
                        value={customDomainInput}
                        onChange={(e) => setCustomDomainInput(e.target.value)}
                        placeholder="my-brand.co.uk"
                        className="flex-1 bg-black border border-zinc-800 rounded-xl px-3 py-2 text-xs text-white focus:outline-none"
                      />
                      <button 
                        onClick={() => {
                          setDnsVerified(true);
                          onAddLog('Publish Hub', 'Triggered client DNS SSL check parameters', 'SUCCESS', 'Verification active');
                        }}
                        className="px-3 py-2 bg-purple-900/30 border border-purple-500/20 text-purple-300 text-[10px] font-mono uppercase rounded-xl"
                      >
                        Verify DNS
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2 text-xs font-mono">
                    <span className="text-[10px] text-slate-500 uppercase">Compliance Checklist Metrics</span>
                    <div className="p-3 bg-black rounded-xl border border-zinc-900 space-y-1.5 text-[11px]">
                      <div className="flex justify-between">
                        <span>SSL Encryption</span>
                        <span className={dnsVerified ? 'text-emerald-400' : 'text-amber-500'}>{dnsVerified ? 'Verified Active (TLS 1.3)' : 'Pending Checker'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Commercial Readiness Check</span>
                        <span className="text-emerald-400">Completed 100%</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Accountant Review Alignment</span>
                        <span className="text-amber-400">Pending before Live Inflow Billing</span>
                      </div>
                    </div>
                  </div>

                  <button 
                    onClick={() => {
                      setHostingStatus('Project Published to Nexus Container');
                      onAddLog('Publish Hub', `Successfully published web draft: ${customDomainInput}`, 'SUCCESS', `Active container point mapped on host server`);
                    }}
                    className="w-full py-3 bg-[#e840f3] hover:scale-[1.02] text-white font-mono text-xs font-black uppercase tracking-wider rounded-xl transition-all shadow-[0_0_15px_rgba(232,64,243,0.25)]"
                  >
                    Deploy Draft Site to Live Hosting
                  </button>
                </div>
              </div>

              {/* Right Deployment Logs & Info */}
              <div className="lg:col-span-7 bg-zinc-950/80 border border-zinc-800 rounded-3xl p-6 space-y-4">
                <h3 className="text-xs font-mono font-black uppercase text-amber-500 tracking-wider">Container Deployment State</h3>

                <div className="p-4 bg-black rounded-2xl border border-zinc-900 text-xs font-mono space-y-2">
                  <div className="flex justify-between border-b border-zinc-900 pb-2">
                    <span>Active Domain Point:</span>
                    <strong className="text-[#00ffff]">{customDomainInput}</strong>
                  </div>
                  <div className="flex justify-between border-b border-zinc-900 pb-2 pt-1">
                    <span>Cluster Status:</span>
                    <strong className="text-amber-400">{hostingStatus}</strong>
                  </div>
                  <div className="flex justify-between border-b border-zinc-900 pb-2 pt-1">
                    <span>Deployment Mode:</span>
                    <span>Controlled Node sandbox</span>
                  </div>
                </div>

                <div className="p-3 bg-emerald-950/20 border border-emerald-500/20 text-emerald-300 text-xs rounded-xl flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5 animate-pulse" />
                  <div>
                    <span className="font-bold block">Protected Sandbox Environment Active</span>
                    <p className="text-[11px] text-slate-400 mt-0.5 leading-relaxed">Publishing is secure. Host security filters automatically strip critical server secrets, API key declarations, and raw environment configurations from outbound page bundles.</p>
                  </div>
                </div>
              </div>

            </motion.div>
          )}

          {/* ==================== SCREEN 12: LEARNING & TEMPLATE LIBRARY ==================== */}
          {activeScreen === 'learning' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6 text-left">
              <div className="bg-zinc-950/80 border border-zinc-800 rounded-3xl p-6 space-y-4">
                <div>
                  <h3 className="text-sm font-mono font-black uppercase text-amber-500 tracking-wider">Free Learning & Code Blueprint Library</h3>
                  <p className="text-xs text-slate-400 font-sans">Browse beginner-friendly layouts and prompt structures configured for rapid everyday deployment.</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    { category: 'Beginner AI', title: 'Calculators & Conversion Metrics', desc: 'Step by step setup guides covering Slider widgets, responsive checkboxes, and Stripe direct integrations.' },
                    { category: 'Charity Tools', title: 'Community Donation Page Draft', desc: 'No-friction forms designed for community donations and tracking splits matching 3 nominated beneficiaries.' },
                    { category: 'SEO & Search', title: 'Takeaway Local Optimization Blueprint', desc: 'Local keywords directory structures tailored to decrease page-load lag and boost mobile conversion.' }
                  ].map((temp, idx) => (
                    <div key={idx} className="p-5 bg-black border border-zinc-900 rounded-2xl hover:border-amber-500/30 transition-all space-y-3">
                      <span className="text-[10px] font-mono uppercase bg-yellow-950/40 text-amber-400 border border-amber-500/20 px-2 py-0.5 rounded tracking-widest w-fit block">
                        {temp.category}
                      </span>
                      <h4 className="text-xs font-mono font-black uppercase text-white tracking-wide">{temp.title}</h4>
                      <p className="text-xs text-slate-400 leading-relaxed font-sans">{temp.desc}</p>
                      
                      <button 
                        onClick={() => {
                          setAppIdea(`Configure dynamic ${temp.title} alignment`);
                          onAddLog('Learning & Template Library', `Loaded training blueprint: "${temp.title}"`, 'SUCCESS', 'Parameters initialized');
                          setActiveScreen('app');
                        }}
                        className="text-amber-400 hover:text-amber-300 text-[10px] font-mono uppercase tracking-widest block pt-2 flex items-center gap-1 cursor-pointer"
                      >
                        Load blueprint <ArrowRight className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {/* ==================== SCREEN 13: COMMUNITY IMPACT DASHBOARD ==================== */}
          {activeScreen === 'community' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6 text-left">
              
              {/* Community Benefit Model Disclosure rules panel */}
              <div className="bg-zinc-950/80 border border-zinc-800 rounded-3xl p-6 space-y-4">
                
                {/* Admin Mode Configurations */}
                {isAdminMode && (
                  <div className="p-4 bg-purple-950/20 border border-purple-500/35 rounded-2xl space-y-3">
                    <div className="flex items-center gap-2">
                      <Settings className="w-4 h-4 text-purple-400" />
                      <strong className="text-xs font-mono text-purple-300 uppercase">Admin Hub: Community Alignment Configuration</strong>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                      <div>
                        <label className="text-[10px] text-slate-400 block mb-1">Beneficiary 1 Name</label>
                        <input 
                          type="text" 
                          value={charityConfig.beneficiary1Name}
                          onChange={(e) => setCharityConfig(prev => ({ ...prev, beneficiary1Name: e.target.value }))}
                          className="w-full bg-black border border-zinc-800 px-3 py-1.5 rounded focus:outline-none text-white font-sans"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-slate-400 block mb-1">Beneficiary 2 Name</label>
                        <input 
                          type="text" 
                          value={charityConfig.beneficiary2Name}
                          onChange={(e) => setCharityConfig(prev => ({ ...prev, beneficiary2Name: e.target.value }))}
                          className="w-full bg-black border border-zinc-800 px-3 py-1.5 rounded focus:outline-none text-white font-sans"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-slate-400 block mb-1">Beneficiary 3 Name</label>
                        <input 
                          type="text" 
                          value={charityConfig.beneficiary3Name}
                          onChange={(e) => setCharityConfig(prev => ({ ...prev, beneficiary3Name: e.target.value }))}
                          className="w-full bg-black border border-zinc-800 px-3 py-1.5 rounded focus:outline-none text-white font-sans"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-xs pt-1">
                      <div>
                        <label className="text-[10px] text-slate-400 block mb-1">Calculation basis</label>
                        <input 
                          type="text" 
                          value={charityConfig.calculationBasis}
                          onChange={(e) => setCharityConfig(prev => ({ ...prev, calculationBasis: e.target.value }))}
                          className="w-full bg-black border border-zinc-800 px-3 py-1.5 rounded focus:outline-none text-white"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-slate-400 block mb-1">Time period</label>
                        <input 
                          type="text" 
                          value={charityConfig.timePeriod}
                          onChange={(prev => setCharityConfig(p => ({ ...p, timePeriod: prev.target.value })))}
                          className="w-full bg-black border border-zinc-800 px-3 py-1.5 rounded focus:outline-none text-white"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-slate-400 block mb-1">Public disclosure active</label>
                        <select 
                          value={charityConfig.publicDisclosureEnabled ? 'true' : 'false'}
                          onChange={(e) => setCharityConfig(p => ({ ...p, publicDisclosureEnabled: e.target.value === 'true' }))}
                          className="w-full bg-black border border-zinc-800 px-3 py-1.5 rounded text-white"
                        >
                          <option value="true">Active & Visible</option>
                          <option value="false">Hide Metrics</option>
                        </select>
                      </div>
                    </div>

                    <p className="text-[10px] text-purple-400 italic font-mono pt-1">
                      *Note: Alignments split 3% equally as 1% to each of the three approved beneficiaries. Do not use levy, extraction or tax terminology to respect compliance structures.
                    </p>
                  </div>
                )}

                {/* Public Display Card */}
                {!isCharityConfigIncomplete ? (
                  <div className="p-8 bg-gradient-to-br from-indigo-950/20 via-black to-zinc-950 border border-[#00ffff]/20 rounded-3xl space-y-6 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-cyan-400/5 blur-[50px] rounded-full pointer-events-none"></div>
                    
                    <div className="space-y-2">
                      <span className="text-[10px] font-mono uppercase bg-cyan-955 text-cyan-400 border border-cyan-400/35 px-2.5 py-0.5 rounded tracking-widest w-fit block">
                        Community Impact Disclosure Model
                      </span>
                      <h4 className="text-xl md:text-2xl font-sans font-black text-white uppercase tracking-tight">
                        Nominated Community Allocation Status
                      </h4>
                      <p className="text-xs text-slate-400 max-w-xl">
                        NexoraX may allocate 3% of eligible net receipts to approved community or charitable initiatives, split equally as 1% to each of three nominated causes.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {[
                        { name: charityConfig.beneficiary1Name, percent: '1.0% Allocation Split' },
                        { name: charityConfig.beneficiary2Name, percent: '1.0% Allocation Split' },
                        { name: charityConfig.beneficiary3Name, percent: '1.0% Allocation Split' }
                      ].map((cause, idx) => (
                        <div key={idx} className="p-4 bg-black border border-zinc-900 rounded-2xl relative hover:border-zinc-800 transition-all space-y-2">
                          <span className="bg-amber-950/20 text-amber-400 border border-amber-500/20 text-[9px] font-mono uppercase px-2 py-0.5 rounded tracking-widest w-fit block">
                            Cause {idx + 1}
                          </span>
                          <strong className="block text-xs font-bold text-white font-sans mt-1 leading-normal">{cause.name}</strong>
                          <span className="text-[10px] text-slate-500 font-mono block pt-1">{cause.percent}</span>
                        </div>
                      ))}
                    </div>

                    <div className="pt-4 border-t border-zinc-900 text-[11px] font-mono text-slate-500 space-y-1">
                      <div>Calculation basis: <strong className="text-slate-300">{charityConfig.calculationBasis}</strong> (VAT excluded: <strong className="text-slate-300">{charityConfig.vatExcluded ? 'Yes' : 'No'}</strong>, Refunds/chargebacks excluded: <strong className="text-slate-300">{charityConfig.refundsExcluded ? 'Yes' : 'No'}</strong>)</div>
                      <div>Financial Period Alignment: <strong className="text-slate-300">{charityConfig.timePeriod}</strong>, Contribution Cap: <strong className="text-slate-300">{charityConfig.contributionCap || 'None'}</strong></div>
                    </div>
                  </div>
                ) : (
                  <div className="p-6 bg-amber-950/20 border-2 border-dashed border-amber-500/30 rounded-2xl text-center space-y-3">
                    <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto animate-bounce" />
                    <h4 className="text-sm font-mono text-amber-400 uppercase font-black">Community Allocation Details Required</h4>
                    <p className="text-xs text-slate-300 max-w-md mx-auto">
                      Community disclosure metrics are currently masked. Direct Admin user modes must fill target beneficiary specifications to display community metrics.
                    </p>
                    {!isAdminMode && (
                      <span className="text-[10px] text-slate-500 block font-mono uppercase">
                        (Please enable Admin Mode on the main dashboard tab to access settings)
                      </span>
                    )}
                  </div>
                )}

              </div>
            </motion.div>
          )}

        </div>

      </div>
    </div>
  );
};
